// ============================================================================
// SS3 LITERATURE-IN-ENGLISH — COMPLETE LEARNING MODULE (14 TOPICS)
// NERDC Scheme of Work — First Term to Third Term (no Third Term in scheme)
// ============================================================================

export const WAEC_LITERATURE_LEARN_SS3 = [
  {
    "topic": "Dramatic Personae of Antony and Cleopatra",
    "topicCode": "SS3-LIT-01",
    "module": "Shakespearean Text — Antony and Cleopatra",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Antony and Cleopatra</span> is a tragedy by William Shakespeare, based on Plutarch's account of the love affair between the Roman general Mark Antony and Cleopatra VII, Queen of Egypt. Before studying the plot, a student must know the dramatis personae — the characters — and what each represents in the conflict between Rome (duty, empire, reason) and Egypt (passion, pleasure, excess).\n</div>\n\n<h3 class=\"learn-subheading\">1. The Triumvirs of Rome</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Character</th><th>Role</th></tr></thead>\n    <tbody>\n      <tr><td>Mark Antony</td><td>One of the three rulers (triumvirs) of Rome; torn between duty to Rome and love for Cleopatra.</td></tr>\n      <tr><td>Octavius Caesar</td><td>Another triumvir; cold, calculating, ambitious; Antony's rival who becomes sole ruler of Rome.</td></tr>\n      <tr><td>Lepidus</td><td>The weakest of the three triumvirs; later removed from power by Caesar.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. The Egyptian Court</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Character</th><th>Role</th></tr></thead>\n    <tbody>\n      <tr><td>Cleopatra</td><td>Queen of Egypt; Antony's lover; passionate, witty, manipulative, and ultimately tragic in her suicide.</td></tr>\n      <tr><td>Charmian</td><td>Cleopatra's loyal attendant; dies beside her queen at the end of the play.</td></tr>\n      <tr><td>Iras</td><td>Another of Cleopatra's attendants; also dies with her queen.</td></tr>\n      <tr><td>Mardian</td><td>A eunuch in Cleopatra's court.</td></tr>\n      <tr><td>Alexas</td><td>Cleopatra's attendant and messenger.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Key Supporting Characters</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Character</th><th>Role</th></tr></thead>\n    <tbody>\n      <tr><td>Enobarbus</td><td>Antony's loyal friend and plain-speaking soldier; deserts Antony for Caesar but dies of grief and guilt.</td></tr>\n      <tr><td>Octavia</td><td>Caesar's sister; married to Antony as a political alliance; represents Roman virtue and is ultimately abandoned.</td></tr>\n      <tr><td>Pompey (Sextus Pompeius)</td><td>Son of the great Pompey; challenges the triumvirs for power before making peace with them.</td></tr>\n      <tr><td>Ventidius</td><td>A Roman general under Antony who defeats the Parthians.</td></tr>\n      <tr><td>Agrippa and Maecenas</td><td>Friends and supporters of Octavius Caesar.</td></tr>\n      <tr><td>The Soothsayer</td><td>An Egyptian fortune-teller who warns Antony that Caesar's fortune will always overshadow his.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Symbolic Function of the Characters</h3>\n<p class=\"learn-p\">Shakespeare uses the characters to embody the play's central conflict: <strong>Rome versus Egypt</strong>. Caesar, Octavia, and the Roman soldiers represent duty, discipline, and reason. Cleopatra, her court, and the pleasures of Egypt represent passion, sensuality, and excess. Antony is caught between the two worlds, and his tragedy lies in his inability to reconcile them.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> WAEC frequently asks candidates to describe a character's role or to compare two characters (e.g., Cleopatra and Octavia, or Enobarbus and Antony). Know each character's loyalty, and what they represent in the Rome-versus-Egypt contrast.\n</div>\n",
    "questions": [
      {
        "q": "What is the primary source Shakespeare drew on for Antony and Cleopatra?",
        "o": [
          "The Bible",
          "A French romance",
          "Plutarch's account of Antony and Cleopatra",
          "Homer's Iliad"
        ],
        "a": 2,
        "e": "Shakespeare based the play on Plutarch's account of the love affair between Mark Antony and Cleopatra.",
        "h": "Think of the ancient Greek biographer Shakespeare often used as a source.",
        "yr": "SS3"
      },
      {
        "q": "What is Mark Antony's position in Rome at the start of the play?",
        "o": [
          "A common soldier",
          "A senator with no military role",
          "The sole Emperor of Rome",
          "One of the three triumvirs ruling Rome"
        ],
        "a": 3,
        "e": "Antony is one of the three triumvirs (rulers) of Rome, alongside Octavius Caesar and Lepidus.",
        "h": "Recall the three-man power-sharing arrangement in Rome.",
        "yr": "SS3"
      },
      {
        "q": "Who are the three triumvirs of Rome in the play?",
        "o": [
          "Antony, Caesar, and Lepidus",
          "Antony, Pompey, and Enobarbus",
          "Caesar, Enobarbus, and Agrippa",
          "Antony, Caesar, and Ventidius"
        ],
        "a": 0,
        "e": "The three triumvirs ruling Rome are Mark Antony, Octavius Caesar, and Lepidus.",
        "h": "There are exactly three names sharing power in Rome.",
        "yr": "SS3"
      },
      {
        "q": "Which triumvir is described as the weakest of the three?",
        "o": [
          "Pompey",
          "Lepidus",
          "Antony",
          "Caesar"
        ],
        "a": 1,
        "e": "Lepidus is the weakest of the three triumvirs and is later removed from power by Caesar.",
        "h": "This triumvir eventually loses his position entirely.",
        "yr": "SS3"
      },
      {
        "q": "What ultimately happens to Lepidus?",
        "o": [
          "He marries Cleopatra",
          "He dies in battle against Antony",
          "He is removed from power by Caesar",
          "He becomes sole ruler of Rome"
        ],
        "a": 2,
        "e": "Lepidus is eventually removed from power by Octavius Caesar, leaving Antony and Caesar as rivals.",
        "h": "Think about how the triumvirate breaks down over the play.",
        "yr": "SS3"
      },
      {
        "q": "Who is Cleopatra's most loyal attendant who dies beside her at the end of the play?",
        "o": [
          "Charmian",
          "Mardian",
          "Iras",
          "Alexas"
        ],
        "a": 0,
        "e": "Charmian is Cleopatra's loyal attendant who dies with her queen, straightening her crown at the very end.",
        "h": "This attendant has the final line of the play.",
        "yr": "SS3"
      },
      {
        "q": "Which two attendants die alongside Cleopatra at the play's conclusion?",
        "o": [
          "Iras and Ventidius",
          "Mardian and Alexas",
          "Charmian and Iras",
          "Octavia and Charmian"
        ],
        "a": 2,
        "e": "Charmian and Iras, Cleopatra's attendants, both die alongside their queen at the end of the play.",
        "h": "Both names begin with vowels and are Cleopatra's female attendants.",
        "yr": "SS3"
      },
      {
        "q": "What is Mardian's role in Cleopatra's court?",
        "o": [
          "A Roman general",
          "A Roman senator",
          "A eunuch in Cleopatra's court",
          "Antony's brother"
        ],
        "a": 2,
        "e": "Mardian is a eunuch serving in Cleopatra's Egyptian court.",
        "h": "This character carries a message to Antony later in the play.",
        "yr": "SS3"
      },
      {
        "q": "Who is Antony's loyal friend and plain-speaking soldier who later deserts him?",
        "o": [
          "Enobarbus",
          "Ventidius",
          "Agrippa",
          "Maecenas"
        ],
        "a": 0,
        "e": "Enobarbus is Antony's loyal friend and blunt-speaking soldier who eventually deserts him for Caesar, then dies of guilt.",
        "h": "This character delivers the famous description of Cleopatra on her barge.",
        "yr": "SS3"
      },
      {
        "q": "What happens to Enobarbus after he deserts Antony?",
        "o": [
          "He returns to fight for Egypt",
          "He dies of guilt and grief",
          "He becomes Caesar's chief general",
          "He marries Cleopatra"
        ],
        "a": 1,
        "e": "Enobarbus dies of guilt and grief after deserting Antony, overwhelmed by his master's continued generosity toward him.",
        "h": "Consider the emotional cost of his betrayal.",
        "yr": "SS3"
      },
      {
        "q": "Who is Octavia in relation to the main characters?",
        "o": [
          "A Roman general",
          "Cleopatra's sister",
          "Caesar's sister, married to Antony for political alliance",
          "Antony's daughter"
        ],
        "a": 2,
        "e": "Octavia is Octavius Caesar's sister, who is married to Antony to seal a political alliance between the two triumvirs.",
        "h": "This marriage is arranged for political, not romantic, reasons.",
        "yr": "SS3"
      },
      {
        "q": "What does Octavia represent symbolically in the play?",
        "o": [
          "Foreign betrayal",
          "Roman virtue and duty",
          "Egyptian passion and excess",
          "Military cowardice"
        ],
        "a": 1,
        "e": "Octavia represents Roman virtue, duty, and restraint, in contrast to Cleopatra's passion and excess.",
        "h": "Think about the Rome-versus-Egypt symbolism.",
        "yr": "SS3"
      },
      {
        "q": "Who is Sextus Pompeius (Pompey) in the play?",
        "o": [
          "A Roman senator loyal to Caesar",
          "Cleopatra's brother",
          "The son of the great Pompey, who challenges the triumvirs",
          "Antony's general in Parthia"
        ],
        "a": 2,
        "e": "Pompey is the son of the great Pompey, who challenges the triumvirs for power before making peace with them.",
        "h": "This character later negotiates a treaty with the triumvirs.",
        "yr": "SS3"
      },
      {
        "q": "Which Roman general defeats the Parthians under Antony's command?",
        "o": [
          "Maecenas",
          "Agrippa",
          "Ventidius",
          "Enobarbus"
        ],
        "a": 2,
        "e": "Ventidius is the Roman general under Antony who defeats the Parthians.",
        "h": "This general later holds back from claiming too much personal glory.",
        "yr": "SS3"
      },
      {
        "q": "Who are Agrippa and Maecenas?",
        "o": [
          "Rival kings from Parthia",
          "Antony's sons",
          "Cleopatra's Egyptian priests",
          "Friends and supporters of Octavius Caesar"
        ],
        "a": 3,
        "e": "Agrippa and Maecenas are friends and supporters of Octavius Caesar.",
        "h": "These two Romans help negotiate Antony's marriage to Octavia.",
        "yr": "SS3"
      },
      {
        "q": "What warning does the Soothsayer give Antony?",
        "o": [
          "That Cleopatra will betray him",
          "That his guardian spirit is always overpowered near Caesar's",
          "That Rome will be destroyed",
          "That he will win every battle"
        ],
        "a": 1,
        "e": "The Soothsayer warns Antony that his guardian spirit (genius) is noble but always overpowered when near Caesar's, advising him to stay away from Caesar.",
        "h": "This warning concerns Antony's spiritual fortune compared to Caesar's.",
        "yr": "SS3"
      },
      {
        "q": "What does Rome symbolise in the central conflict of the play?",
        "o": [
          "Passion and pleasure",
          "Duty, empire, and reason",
          "Chaos and disorder",
          "Foreign invasion"
        ],
        "a": 1,
        "e": "Rome represents duty, empire, and reason, in contrast to Egypt's passion and excess.",
        "h": "Consider what Caesar and Octavia stand for.",
        "yr": "SS3"
      },
      {
        "q": "What does Egypt symbolise in the central conflict of the play?",
        "o": [
          "Passion, pleasure, and excess",
          "Cold political calculation",
          "Military order",
          "Duty and discipline"
        ],
        "a": 0,
        "e": "Egypt, embodied by Cleopatra and her court, represents passion, sensuality, and excess.",
        "h": "Consider what Cleopatra and her court represent.",
        "yr": "SS3"
      },
      {
        "q": "Why is Antony's character considered tragic in relation to Rome and Egypt?",
        "o": [
          "He successfully balances both worlds without conflict",
          "He has no connection to either Rome or Egypt",
          "He fully embraces Rome and rejects Egypt",
          "He is caught between the two worlds and cannot reconcile them"
        ],
        "a": 3,
        "e": "Antony's tragedy lies in being caught between Rome and Egypt, unable to reconcile his duty with his passion.",
        "h": "This is the core of his internal conflict throughout the play.",
        "yr": "SS3"
      },
      {
        "q": "Which character serves as Cleopatra's attendant and messenger?",
        "o": [
          "Agrippa",
          "Alexas",
          "Ventidius",
          "Eros"
        ],
        "a": 1,
        "e": "Alexas is Cleopatra's attendant and messenger in the Egyptian court.",
        "h": "This character's name is distinct from the Roman military figures.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Antony and Cleopatra – Act I",
    "topicCode": "SS3-LIT-02",
    "module": "Shakespearean Text — Antony and Cleopatra",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Act I introduces the central conflict of the play: Antony's divided loyalty between the duties of Rome and his passion for Cleopatra in Egypt.\n</div>\n\n<h3 class=\"learn-subheading\">1. Opening Scene — Philo's Complaint</h3>\n<p class=\"learn-p\">The play opens with the Roman soldier <strong>Philo</strong> complaining to Demetrius that Antony's love for Cleopatra has made him neglect his duties as a soldier and triumvir. Philo describes Antony's eyes, which once looked like Mars surveying war, as now doting on a \"tawny front\" (Cleopatra). This sets the Rome-versus-Egypt theme from the very first lines.</p>\n\n<h3 class=\"learn-subheading\">2. Antony and Cleopatra's Love Display</h3>\n<p class=\"learn-p\">Antony declares that his love for Cleopatra cannot be measured — <strong>\"there's beggary in the love that can be reckoned.\"</strong> Cleopatra teases and tests him by asking how much he loves her, and mocks the arrival of messengers from Rome, urging Antony to hear the news. Antony dismisses Rome, declaring \"Let Rome in Tiber melt,\" showing he is (at this point) choosing Egypt over duty.</p>\n\n<h3 class=\"learn-subheading\">3. News from Rome</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>News</th><th>Significance</th></tr></thead>\n    <tbody>\n      <tr><td>Fulvia (Antony's wife) has died</td><td>Removes one obstacle to Antony's freedom, but also a reminder of his neglected Roman responsibilities.</td></tr>\n      <tr><td>Sextus Pompeius is gathering forces at sea</td><td>A military threat to the triumvirate that demands Antony's attention.</td></tr>\n      <tr><td>Fulvia and Antony's brother had waged war against Caesar</td><td>Shows the political instability Antony has ignored while in Egypt.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Antony's Decision to Return to Rome</h3>\n<p class=\"learn-p\">After hearing the news, Antony resolves to break free of Egypt's \"strong Egyptian fetters\" and return to Rome, admitting that he \"must from this enchanting queen break off.\" This shows the beginning of his internal struggle — duty calling him back even as his heart remains with Cleopatra.</p>\n\n<h3 class=\"learn-subheading\">5. Cleopatra's Reaction and Enobarbus's Commentary</h3>\n<p class=\"learn-p\">Cleopatra is furious and heartbroken when Antony announces his departure, accusing him of never truly loving her and mocking his excuses. <strong>Enobarbus</strong>, Antony's blunt-speaking friend, offers a cynical, comic commentary throughout, observing that Cleopatra's moods and \"infinite variety\" make her impossible to predict or resist.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Key Theme in Act I:</strong> The conflict between <em>reason and passion</em>, and between <em>public duty and private love</em>, is established immediately. Antony is torn from the play's opening moments.\n</div>\n",
    "questions": [
      {
        "q": "Who opens the play complaining about Antony's neglect of duty?",
        "o": [
          "Philo",
          "Demetrius",
          "Enobarbus",
          "Caesar"
        ],
        "a": 0,
        "e": "The play opens with the Roman soldier Philo complaining to Demetrius about Antony's neglect of his duties because of his love for Cleopatra.",
        "h": "This character speaks the very first lines of the play.",
        "yr": "SS3"
      },
      {
        "q": "How does Philo describe the change in Antony's eyes?",
        "o": [
          "They now look like Mars surveying war",
          "They once looked like Mars surveying war, but now dote on Cleopatra",
          "They have gone permanently blind",
          "They show no change at all"
        ],
        "a": 1,
        "e": "Philo says Antony's eyes once looked like Mars surveying war but now dote on a 'tawny front,' referring to Cleopatra.",
        "h": "Consider the contrast Philo draws between the past and present.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony say about measuring his love for Cleopatra?",
        "o": [
          "That it can be easily counted",
          "That there is 'beggary' in a love that can be reckoned",
          "That Roman law forbids such love",
          "That he does not love her at all"
        ],
        "a": 1,
        "e": "Antony declares that 'there's beggary in the love that can be reckoned,' meaning true love cannot be measured or counted.",
        "h": "This is Antony's famous declaration about the immeasurability of love.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony declare about Rome in Act I?",
        "o": [
          "That Rome must be protected at all costs",
          "'Let Rome in Tiber melt'",
          "That Rome no longer exists",
          "That he will return to Rome immediately"
        ],
        "a": 1,
        "e": "Antony dismisses Rome, declaring 'Let Rome in Tiber melt,' showing he initially chooses Egypt over Roman duty.",
        "h": "This famous line shows Antony rejecting his Roman responsibilities.",
        "yr": "SS3"
      },
      {
        "q": "What news arrives from Rome about Antony's wife?",
        "o": [
          "Fulvia has become a soldier",
          "Fulvia has died",
          "Fulvia has remarried",
          "Fulvia has joined Cleopatra's court"
        ],
        "a": 1,
        "e": "News arrives that Fulvia, Antony's wife, has died.",
        "h": "This news removes one obstacle but reminds Antony of his neglected duties.",
        "yr": "SS3"
      },
      {
        "q": "What military threat is reported to Antony in Act I?",
        "o": [
          "The Parthians have captured Rome",
          "Cleopatra is amassing an army",
          "Sextus Pompeius is gathering forces at sea",
          "Caesar has already invaded Egypt"
        ],
        "a": 2,
        "e": "Sextus Pompeius is reported to be gathering forces at sea, posing a military threat to the triumvirate.",
        "h": "This threat comes from the son of the great Pompey.",
        "yr": "SS3"
      },
      {
        "q": "What had Fulvia and Antony's brother done before the play begins?",
        "o": [
          "Negotiated peace with Pompey",
          "Fled to Egypt",
          "Supported Cleopatra's rule",
          "Waged war against Caesar"
        ],
        "a": 3,
        "e": "Fulvia and Antony's brother had waged war against Caesar, showing the political instability Antony ignored while in Egypt.",
        "h": "This reveals the political chaos Antony has been neglecting.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony resolve to do after hearing the news from Rome?",
        "o": [
          "Marry Cleopatra immediately",
          "Stay permanently in Egypt",
          "Break free of Egypt's 'strong Egyptian fetters' and return to Rome",
          "Abandon his position as triumvir"
        ],
        "a": 2,
        "e": "Antony resolves to break free of Egypt's 'strong Egyptian fetters' and return to Rome to address the crisis.",
        "h": "Consider Antony's sense of duty beginning to reassert itself.",
        "yr": "SS3"
      },
      {
        "q": "What phrase does Antony use to describe Cleopatra when deciding to leave?",
        "o": [
          "'This gentle queen'",
          "'This enchanting queen'",
          "'This loyal queen'",
          "'This foolish queen'"
        ],
        "a": 1,
        "e": "Antony admits he 'must from this enchanting queen break off,' acknowledging Cleopatra's powerful hold over him.",
        "h": "The word describes Cleopatra's captivating power over Antony.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra react when Antony announces his departure?",
        "o": [
          "She calmly wishes him well",
          "She is furious and heartbroken, accusing him of never truly loving her",
          "She immediately leaves for Rome herself",
          "She threatens to join Caesar's army"
        ],
        "a": 1,
        "e": "Cleopatra is furious and heartbroken, accusing Antony of never truly loving her and mocking his excuses.",
        "h": "Consider Cleopatra's emotional and volatile nature.",
        "yr": "SS3"
      },
      {
        "q": "How does Enobarbus describe Cleopatra's unpredictable moods?",
        "o": [
          "As having 'infinite variety'",
          "As dull and predictable",
          "As entirely fake",
          "As identical to Octavia's"
        ],
        "a": 0,
        "e": "Enobarbus observes that Cleopatra's moods and 'infinite variety' make her impossible to predict or resist.",
        "h": "This famous phrase describes Cleopatra's endless capacity to fascinate.",
        "yr": "SS3"
      },
      {
        "q": "What role does Enobarbus play in Act I's tone?",
        "o": [
          "He is entirely silent throughout",
          "He negotiates directly with Caesar",
          "He offers cynical, comic commentary on events",
          "He fights in a major battle"
        ],
        "a": 2,
        "e": "Enobarbus offers a cynical, comic commentary throughout Act I, observing the relationship between Antony and Cleopatra.",
        "h": "Consider his role as an outside observer with a dry wit.",
        "yr": "SS3"
      },
      {
        "q": "What theme is established from the very opening lines of the play?",
        "o": [
          "The rise of a new Roman emperor",
          "A celebration of Antony's leadership",
          "A war between Egypt and Parthia",
          "The Rome-versus-Egypt conflict"
        ],
        "a": 3,
        "e": "The Rome-versus-Egypt theme is established from the opening lines, through Philo's complaint about Antony's neglect.",
        "h": "This is the central symbolic conflict of the entire play.",
        "yr": "SS3"
      },
      {
        "q": "Why does Cleopatra mock the arrival of messengers from Rome?",
        "o": [
          "Because she supports Caesar",
          "Because she wants to send Antony away",
          "To urge Antony to hear the news",
          "Because she has never met a Roman messenger before"
        ],
        "a": 2,
        "e": "Cleopatra mocks the arrival of messengers to urge Antony to hear the news from Rome, testing his priorities.",
        "h": "Consider Cleopatra's playful, manipulative behaviour toward Antony.",
        "yr": "SS3"
      },
      {
        "q": "What does Cleopatra do to test how much Antony loves her?",
        "o": [
          "She demands he divorce Fulvia",
          "She asks him how much he loves her",
          "She asks him to fight a battle for her",
          "She sends him to Parthia"
        ],
        "a": 1,
        "e": "Cleopatra teases and tests Antony by asking him how much he loves her.",
        "h": "This is a playful but pointed test of Antony's devotion.",
        "yr": "SS3"
      },
      {
        "q": "What does the removal of Fulvia as an obstacle also represent?",
        "o": [
          "Cleopatra's total victory over Rome",
          "A reminder of Antony's neglected Roman responsibilities",
          "A permanent end to Antony's connection with Rome",
          "The end of the triumvirate"
        ],
        "a": 1,
        "e": "While Fulvia's death removes an obstacle to Antony's freedom, it also reminds him of his neglected Roman responsibilities.",
        "h": "Consider the dual significance of this news for Antony.",
        "yr": "SS3"
      },
      {
        "q": "What is the significance of Antony's internal struggle in Act I?",
        "o": [
          "It shows the beginning of his conflict between duty and passion",
          "It shows he feels nothing for either Rome or Egypt",
          "It proves he has fully chosen Egypt",
          "It proves he has fully chosen Rome"
        ],
        "a": 0,
        "e": "Antony's internal struggle in Act I marks the beginning of his conflict between duty to Rome and love for Cleopatra.",
        "h": "This struggle continues to develop throughout the play.",
        "yr": "SS3"
      },
      {
        "q": "What does Philo's opening speech suggest about Antony's former reputation?",
        "o": [
          "He was always devoted only to Egypt",
          "He was never a respected soldier",
          "He had no military reputation at all",
          "He was once compared to the god of war, Mars"
        ],
        "a": 3,
        "e": "Philo's speech suggests Antony was once compared to Mars, the god of war, before his love for Cleopatra changed him.",
        "h": "Consider the mythological comparison used to describe Antony's past greatness.",
        "yr": "SS3"
      },
      {
        "q": "What is the dramatic function of the news from Rome arriving early in Act I?",
        "o": [
          "It has no effect on the plot",
          "It resolves the play's central conflict immediately",
          "It causes Cleopatra to leave Egypt",
          "It forces Antony to confront the conflict between Rome and Egypt"
        ],
        "a": 3,
        "e": "The news from Rome forces Antony to confront the central conflict between his Roman duties and his Egyptian passion.",
        "h": "This news is the catalyst for Antony's internal and external conflict.",
        "yr": "SS3"
      },
      {
        "q": "Which phrase best captures Cleopatra's emotional response to being told Antony must leave?",
        "o": [
          "Furious heartbreak and accusation",
          "Immediate forgiveness",
          "Complete indifference",
          "Calm acceptance"
        ],
        "a": 0,
        "e": "Cleopatra's response to Antony's departure is one of furious heartbreak, accusing him of insincerity.",
        "h": "Consider the intensity of Cleopatra's reaction described in the text.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Antony and Cleopatra – Act II",
    "topicCode": "SS3-LIT-03",
    "module": "Shakespearean Text — Antony and Cleopatra",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  In Act II, Antony returns to Rome, political marriages are arranged, and Enobarbus delivers his famous description of Cleopatra on her barge.\n</div>\n\n<h3 class=\"learn-subheading\">1. Pompey's Rebellion</h3>\n<p class=\"learn-p\">Pompey believes he has the advantage over the triumvirs because Antony is distracted by Egypt. He is disappointed to learn that Antony has left Egypt and is returning to Rome, weakening his hoped-for advantage.</p>\n\n<h3 class=\"learn-subheading\">2. Reconciliation Between Antony and Caesar</h3>\n<p class=\"learn-p\">Antony and Caesar quarrel over Antony's neglect of his duties, particularly regarding Fulvia's war against Caesar. Through the diplomacy of Agrippa and Maecenas, the two triumvirs reconcile — sealed by a political marriage: Antony agrees to marry <strong>Octavia</strong>, Caesar's sister, to strengthen the alliance between Rome's two most powerful men.</p>\n\n<h3 class=\"learn-subheading\">3. Enobarbus's Description of Cleopatra on the Barge</h3>\n<p class=\"learn-p\">In one of the most celebrated speeches in the play, Enobarbus describes to Agrippa and Maecenas how Cleopatra first met Antony, arriving on a magnificent barge with a burnished golden poop, purple sails, and silver oars, dressed as Venus, fanned by boys dressed as Cupids. He famously says Cleopatra's <strong>\"infinite variety\"</strong> means custom cannot make her ordinary — age cannot wither her, nor can familiarity ever exhaust her charm.</p>\n\n<h3 class=\"learn-subheading\">4. The Treaty with Pompey</h3>\n<p class=\"learn-p\">The triumvirs meet with Pompey and negotiate a peace treaty, granting him Sicily and Sardinia in exchange for his ceasing his rebellion and clearing the seas of pirates. At the celebration feast aboard Pompey's galley, Menas secretly suggests to Pompey that he could kill the triumvirs and rule Rome alone, but Pompey refuses, saying he wishes Menas had done it without telling him first — showing his own moral compromise.</p>\n\n<h3 class=\"learn-subheading\">5. Antony's Marriage to Octavia and the Soothsayer's Warning</h3>\n<p class=\"learn-p\">Despite marrying Octavia, an Egyptian <strong>Soothsayer</strong> warns Antony that his guardian spirit (genius) is noble, but always overpowered when near Caesar's — advising him to keep away from Caesar and stay in the East. This foreshadows Antony's eventual return to Cleopatra and his downfall.</p>\n\n<h3 class=\"learn-subheading\">6. Cleopatra's Reaction to News of the Marriage</h3>\n<p class=\"learn-p\">Back in Egypt, Cleopatra learns of Antony's marriage to Octavia from a messenger. She violently threatens the messenger, then, once calmer, interrogates him about Octavia's appearance, comforting herself with the idea that Octavia is dull, short, and low-voiced compared to her.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> The Enobarbus \"barge speech\" (Act II Scene ii) is one of the most frequently quoted and examined passages in the whole play — know its imagery and the phrase \"infinite variety\" well.\n</div>\n",
    "questions": [
      {
        "q": "Why is Pompey initially confident about his chances against the triumvirs?",
        "o": [
          "Cleopatra has promised to support him",
          "He has already defeated Caesar",
          "He believes Antony is distracted by Egypt",
          "He has more soldiers than all three triumvirs combined"
        ],
        "a": 2,
        "e": "Pompey believes he has the advantage because Antony is distracted by Egypt and not focused on Roman affairs.",
        "h": "Consider what Pompey assumes about Antony's attention.",
        "yr": "SS3"
      },
      {
        "q": "What disappoints Pompey early in Act II?",
        "o": [
          "Learning that Antony has left Egypt and is returning to Rome",
          "Learning that Caesar has surrendered",
          "Discovering Cleopatra has died",
          "Losing his fleet in a storm"
        ],
        "a": 0,
        "e": "Pompey is disappointed to learn that Antony has left Egypt and is returning to Rome, weakening his hoped-for advantage.",
        "h": "This news changes Pompey's calculation of his chances.",
        "yr": "SS3"
      },
      {
        "q": "What causes the quarrel between Antony and Caesar in Act II?",
        "o": [
          "Caesar's refusal to share power with Lepidus",
          "Antony's refusal to fight Pompey",
          "Antony's neglect of duty and Fulvia's war against Caesar",
          "A dispute over Cleopatra's throne"
        ],
        "a": 2,
        "e": "Antony and Caesar quarrel over Antony's neglect of his duties, particularly regarding Fulvia's war against Caesar.",
        "h": "This concerns actions taken while Antony was in Egypt.",
        "yr": "SS3"
      },
      {
        "q": "Who helps negotiate the reconciliation between Antony and Caesar?",
        "o": [
          "Agrippa and Maecenas",
          "Enobarbus and Ventidius",
          "Charmian and Iras",
          "The Soothsayer and Mardian"
        ],
        "a": 0,
        "e": "Agrippa and Maecenas, Caesar's supporters, help negotiate the reconciliation between Antony and Caesar.",
        "h": "These two Romans are described as friends of Caesar.",
        "yr": "SS3"
      },
      {
        "q": "How is the reconciliation between Antony and Caesar sealed?",
        "o": [
          "Through Cleopatra's abdication",
          "Through Antony's marriage to Octavia, Caesar's sister",
          "Through a treaty giving Antony all of Rome",
          "Through a duel between the two men"
        ],
        "a": 1,
        "e": "The reconciliation is sealed through Antony's marriage to Octavia, Caesar's sister, strengthening their political alliance.",
        "h": "This marriage is a political strategy, not a romantic choice.",
        "yr": "SS3"
      },
      {
        "q": "Who delivers the famous speech describing Cleopatra's first meeting with Antony?",
        "o": [
          "Maecenas",
          "Agrippa",
          "Caesar",
          "Enobarbus"
        ],
        "a": 3,
        "e": "Enobarbus delivers the famous speech to Agrippa and Maecenas, describing Cleopatra's arrival on her barge.",
        "h": "This character earlier offered cynical commentary in Act I.",
        "yr": "SS3"
      },
      {
        "q": "How does Enobarbus describe Cleopatra's barge?",
        "o": [
          "Made entirely of wood with no decoration",
          "Sunk beneath the water",
          "Plain and simple",
          "With a burnished golden poop, purple sails, and silver oars"
        ],
        "a": 3,
        "e": "Enobarbus describes the barge as having a burnished golden poop, purple sails, and silver oars, dressed lavishly to resemble Venus's court.",
        "h": "Consider the opulent imagery used in this famous description.",
        "yr": "SS3"
      },
      {
        "q": "How was Cleopatra dressed when Enobarbus first saw her on the barge?",
        "o": [
          "As a humble servant",
          "As a Roman soldier",
          "As Venus, fanned by boys dressed as Cupids",
          "As Octavia's attendant"
        ],
        "a": 2,
        "e": "Cleopatra was dressed as Venus, fanned by boys dressed as Cupids, in Enobarbus's description of her arrival.",
        "h": "This mythological comparison emphasises her beauty and allure.",
        "yr": "SS3"
      },
      {
        "q": "What does Enobarbus mean by Cleopatra's 'infinite variety'?",
        "o": [
          "That she has infinite wealth",
          "That she looks different every day physically",
          "That she speaks many languages",
          "That custom cannot make her stale, and age cannot wither her ever-changing charm"
        ],
        "a": 3,
        "e": "Enobarbus means that age cannot wither Cleopatra, nor can familiarity ever exhaust her ever-changing charm and appeal.",
        "h": "This phrase captures Cleopatra's endlessly fascinating character.",
        "yr": "SS3"
      },
      {
        "q": "What territories does Pompey receive in the treaty with the triumvirs?",
        "o": [
          "Rome and Athens",
          "Gaul and Britain",
          "Sicily and Sardinia",
          "Egypt and Parthia"
        ],
        "a": 2,
        "e": "Pompey is granted Sicily and Sardinia in exchange for ceasing his rebellion and clearing the seas of pirates.",
        "h": "These are Mediterranean territories offered as part of the peace deal.",
        "yr": "SS3"
      },
      {
        "q": "What does Pompey agree to do in exchange for Sicily and Sardinia?",
        "o": [
          "Join forces with Antony against Caesar",
          "Surrender all his ships to Caesar",
          "Marry Octavia",
          "Cease his rebellion and clear the seas of pirates"
        ],
        "a": 3,
        "e": "Pompey agrees to cease his rebellion and clear the seas of pirates in exchange for receiving Sicily and Sardinia.",
        "h": "This concerns Pompey's naval and military role.",
        "yr": "SS3"
      },
      {
        "q": "What does Menas secretly suggest to Pompey during the feast?",
        "o": [
          "That he flee to Egypt",
          "That he should marry Cleopatra",
          "That he surrender immediately",
          "That he kill the triumvirs and rule Rome alone"
        ],
        "a": 3,
        "e": "Menas secretly suggests to Pompey that he could kill the triumvirs while they are aboard his galley and rule Rome alone.",
        "h": "This is a treacherous suggestion made during the celebratory feast.",
        "yr": "SS3"
      },
      {
        "q": "How does Pompey respond to Menas's suggestion?",
        "o": [
          "He agrees immediately and orders the killing",
          "He refuses, but says he wishes Menas had done it without telling him first",
          "He has Menas executed on the spot",
          "He reports Menas to Caesar"
        ],
        "a": 1,
        "e": "Pompey refuses, but says he wishes Menas had done it without telling him first, showing his own moral compromise.",
        "h": "Consider the hypocrisy in Pompey's response.",
        "yr": "SS3"
      },
      {
        "q": "What does Pompey's response to Menas reveal about his character?",
        "o": [
          "Total loyalty to the triumvirs",
          "Complete moral integrity",
          "A degree of moral compromise despite refusing outright",
          "Total ignorance of the plot"
        ],
        "a": 2,
        "e": "Pompey's response reveals a degree of moral compromise — he refuses the act but wishes it had happened without his direct involvement.",
        "h": "This shows a gap between his stated values and private wishes.",
        "yr": "SS3"
      },
      {
        "q": "What warning does the Soothsayer give Antony after his marriage to Octavia?",
        "o": [
          "To attack Caesar immediately",
          "To divorce Octavia immediately",
          "To stay away from Caesar and remain in the East",
          "To trust Caesar completely"
        ],
        "a": 2,
        "e": "The Soothsayer warns Antony to keep away from Caesar and stay in the East, since his spirit is always overpowered near Caesar's.",
        "h": "This warning foreshadows Antony's return to Cleopatra.",
        "yr": "SS3"
      },
      {
        "q": "What does the Soothsayer's warning foreshadow?",
        "o": [
          "Pompey's victory over Rome",
          "Antony's eventual return to Cleopatra and his downfall",
          "Octavia's betrayal of Antony",
          "Caesar's death"
        ],
        "a": 1,
        "e": "The Soothsayer's warning foreshadows Antony's eventual return to Cleopatra and his ultimate downfall.",
        "h": "Consider how this warning relates to the play's tragic ending.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra first learn of Antony's marriage to Octavia?",
        "o": [
          "From a letter written by Antony himself",
          "From a messenger",
          "From Enobarbus directly",
          "From Caesar's official announcement"
        ],
        "a": 1,
        "e": "Cleopatra learns of Antony's marriage to Octavia from a messenger who brings the news to Egypt.",
        "h": "This news arrives to Cleopatra secondhand, provoking a strong reaction.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra initially react to the news of Antony's marriage?",
        "o": [
          "She violently threatens the messenger",
          "She calmly congratulates Antony",
          "She immediately sails to Rome",
          "She writes to Octavia"
        ],
        "a": 0,
        "e": "Cleopatra violently threatens the messenger upon hearing the news of Antony's marriage to Octavia.",
        "h": "Consider Cleopatra's volatile temperament when hearing unwelcome news.",
        "yr": "SS3"
      },
      {
        "q": "Once calmer, what does Cleopatra ask the messenger about Octavia?",
        "o": [
          "Octavia's military skills",
          "Octavia's appearance",
          "Octavia's wealth",
          "Octavia's political opinions"
        ],
        "a": 1,
        "e": "Once calmer, Cleopatra interrogates the messenger about Octavia's physical appearance, comparing herself to her rival.",
        "h": "This reveals Cleopatra's insecurity and rivalry with Octavia.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra comfort herself regarding Octavia?",
        "o": [
          "By deciding Octavia is more beautiful than she is",
          "By ignoring the matter entirely",
          "By planning to meet Octavia in person immediately",
          "By believing Octavia is dull, short, and low-voiced compared to her"
        ],
        "a": 3,
        "e": "Cleopatra comforts herself with the belief that Octavia is dull, short, and low-voiced compared to her own vibrant beauty.",
        "h": "Consider how Cleopatra copes with jealousy through comparison.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Antony and Cleopatra – Act III",
    "topicCode": "SS3-LIT-04",
    "module": "Shakespearean Text — Antony and Cleopatra",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Act III marks the turning point of the play: Antony breaks his political marriage, the alliance with Caesar collapses, and the disastrous naval Battle of Actium takes place.\n</div>\n\n<h3 class=\"learn-subheading\">1. Ventidius's Victory Over the Parthians</h3>\n<p class=\"learn-p\">Antony's general <strong>Ventidius</strong> defeats the Parthians but deliberately holds back from pursuing total victory, fearing that outshining Antony's own military glory could damage his career. This scene shows the danger of overshadowing a great but insecure leader.</p>\n\n<h3 class=\"learn-subheading\">2. The Collapse of the Antony–Octavia Marriage</h3>\n<p class=\"learn-p\">Octavia is caught between her husband Antony and her brother Caesar as tensions rise again. Caesar accuses Antony of insulting Octavia by returning to Cleopatra in Egypt and resuming his old life there, and of illegally distributing Roman territories to Cleopatra and her children (the \"Donations of Alexandria\"). Antony formally divorces Octavia in all but name by abandoning her and returning fully to Cleopatra.</p>\n\n<h3 class=\"learn-subheading\">3. War Declared Between Antony and Caesar</h3>\n<p class=\"learn-p\">Caesar declares war on Antony, condemning his behaviour as unworthy of a Roman. Antony, against the advice of his experienced soldiers (including Enobarbus), decides to fight Caesar <strong>at sea</strong> rather than on land, mainly because Cleopatra insists on it and offers her own ships.</p>\n\n<h3 class=\"learn-subheading\">4. The Battle of Actium</h3>\n<p class=\"learn-p\">At the decisive naval Battle of Actium, Cleopatra's ship flees the battle without warning, and Antony — humiliatingly — follows her, abandoning his fleet and forces mid-battle. This is the single most catastrophic decision in the play: it costs Antony the battle, his reputation, and much of his military support.</p>\n\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Character</th><th>Reaction to the Defeat</th></tr></thead>\n    <tbody>\n      <tr><td>Antony</td><td>Deeply ashamed; admits he has lost his honour by following Cleopatra instead of leading his men.</td></tr>\n      <tr><td>Enobarbus</td><td>Horrified and disillusioned; begins to consider abandoning Antony's cause.</td></tr>\n      <tr><td>Cleopatra</td><td>Begs Antony's forgiveness for fleeing; he forgives her, showing his love outweighs his reason.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">5. Antony's Desperate Diplomacy</h3>\n<p class=\"learn-p\">Antony sends a message to Caesar asking to be allowed to live privately in Egypt or in Athens, but Caesar refuses and instead sends Thidias to try to persuade Cleopatra to abandon Antony. When Antony discovers Thidias kissing Cleopatra's hand, he furiously has him whipped, showing his jealousy and declining self-control.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Key Theme in Act III:</strong> The Battle of Actium is the play's turning point — Antony's choice to follow Cleopatra rather than fight shows how passion has completely overtaken reason and military judgement.\n</div>\n",
    "questions": [
      {
        "q": "What does Ventidius achieve in Act III?",
        "o": [
          "He loses to the Parthians",
          "He kills Pompey",
          "He captures Caesar",
          "He defeats the Parthians"
        ],
        "a": 3,
        "e": "Ventidius, Antony's general, defeats the Parthians in Act III.",
        "h": "Recall this general's earlier introduction in the play.",
        "yr": "SS3"
      },
      {
        "q": "Why does Ventidius hold back from total victory over the Parthians?",
        "o": [
          "He fears outshining Antony's own military glory",
          "He runs out of soldiers",
          "He is ordered to retreat by Caesar",
          "He is secretly loyal to Pompey"
        ],
        "a": 0,
        "e": "Ventidius deliberately holds back from pursuing total victory, fearing that outshining Antony's own glory could damage his career.",
        "h": "Consider the danger of overshadowing an insecure leader.",
        "yr": "SS3"
      },
      {
        "q": "What does the Ventidius episode illustrate about Antony as a leader?",
        "o": [
          "His complete confidence in his generals",
          "His insecurity about being outshone by subordinates",
          "His generosity toward rivals",
          "His indifference to military success"
        ],
        "a": 1,
        "e": "The episode illustrates the danger of overshadowing a great but insecure leader like Antony.",
        "h": "Think about why Ventidius holds back rather than pursuing full victory.",
        "yr": "SS3"
      },
      {
        "q": "What accusation does Caesar make against Antony regarding Octavia?",
        "o": [
          "That Antony wants to divorce her publicly",
          "That Antony has been too kind to her",
          "That Antony refuses to see her",
          "That Antony has insulted her by returning to Cleopatra"
        ],
        "a": 3,
        "e": "Caesar accuses Antony of insulting Octavia by returning to Cleopatra in Egypt and resuming his old life there.",
        "h": "This accusation concerns Antony's treatment of Caesar's sister.",
        "yr": "SS3"
      },
      {
        "q": "What are the 'Donations of Alexandria' that anger Caesar?",
        "o": [
          "Antony's donation of ships to Pompey",
          "A peace treaty between Egypt and Parthia",
          "Antony's illegal distribution of Roman territories to Cleopatra and her children",
          "A gift of gold from Cleopatra to Rome"
        ],
        "a": 2,
        "e": "The Donations of Alexandria refer to Antony's illegal distribution of Roman territories to Cleopatra and her children, angering Caesar.",
        "h": "This concerns Antony giving away Roman-controlled lands.",
        "yr": "SS3"
      },
      {
        "q": "How does Antony effectively end his marriage to Octavia?",
        "o": [
          "By formally divorcing her in a Roman court",
          "By sending her to Egypt",
          "By abandoning her and returning fully to Cleopatra",
          "By having her killed"
        ],
        "a": 2,
        "e": "Antony effectively ends the marriage by abandoning Octavia and returning fully to Cleopatra, without a formal divorce.",
        "h": "Consider how this action is described as a divorce 'in all but name.'",
        "yr": "SS3"
      },
      {
        "q": "What does Caesar do in response to Antony's behaviour?",
        "o": [
          "Retires from politics",
          "Forgives Antony completely",
          "Declares war on Antony",
          "Marries Cleopatra himself"
        ],
        "a": 2,
        "e": "Caesar declares war on Antony, condemning his behaviour as unworthy of a Roman.",
        "h": "This is the formal political and military response to Antony's actions.",
        "yr": "SS3"
      },
      {
        "q": "Where does Antony decide to fight Caesar, against his soldiers' advice?",
        "o": [
          "In Rome itself",
          "At sea",
          "On land",
          "In Parthia"
        ],
        "a": 1,
        "e": "Antony decides to fight at sea rather than on land, against the advice of his experienced soldiers.",
        "h": "Consider Cleopatra's influence on this military decision.",
        "yr": "SS3"
      },
      {
        "q": "Why does Antony choose to fight at sea?",
        "o": [
          "Because Cleopatra insists on it and offers her own ships",
          "Because Rome has no land army available",
          "Because Caesar has no navy",
          "Because his soldiers strongly recommend it"
        ],
        "a": 0,
        "e": "Antony chooses to fight at sea mainly because Cleopatra insists on it and offers her own ships for the battle.",
        "h": "This decision reflects Cleopatra's influence over Antony's military judgement.",
        "yr": "SS3"
      },
      {
        "q": "What happens to Cleopatra's ship during the Battle of Actium?",
        "o": [
          "It leads the fleet to victory",
          "It sinks immediately",
          "It flees the battle without warning",
          "It is captured by Caesar"
        ],
        "a": 2,
        "e": "Cleopatra's ship flees the Battle of Actium without warning, abandoning the fight.",
        "h": "This is the single most catastrophic event of the battle.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony do when Cleopatra's ship flees at Actium?",
        "o": [
          "He orders her ship to be sunk",
          "He follows her, abandoning his fleet and forces",
          "He continues fighting without her",
          "He surrenders to Caesar immediately"
        ],
        "a": 1,
        "e": "Antony humiliatingly follows Cleopatra's fleeing ship, abandoning his own fleet and forces mid-battle.",
        "h": "This decision is considered the play's most catastrophic turning point.",
        "yr": "SS3"
      },
      {
        "q": "What is the significance of Antony following Cleopatra at Actium?",
        "o": [
          "It has no real consequence",
          "It costs him the battle, his reputation, and much of his support",
          "It secures his victory over Caesar",
          "It strengthens his alliance with Rome"
        ],
        "a": 1,
        "e": "Following Cleopatra costs Antony the battle, his reputation, and much of his military support — a catastrophic turning point.",
        "h": "Consider the far-reaching consequences of this single decision.",
        "yr": "SS3"
      },
      {
        "q": "How does Antony react after the defeat at Actium?",
        "o": [
          "He celebrates the outcome",
          "He shows no reaction at all",
          "He is deeply ashamed and admits he lost his honour",
          "He blames Caesar entirely"
        ],
        "a": 2,
        "e": "Antony is deeply ashamed after Actium, admitting he has lost his honour by following Cleopatra instead of leading his men.",
        "h": "Consider Antony's emotional and moral response to his own failure.",
        "yr": "SS3"
      },
      {
        "q": "How does Enobarbus react to Antony's defeat at Actium?",
        "o": [
          "He immediately joins Pompey's forces",
          "He celebrates the defeat",
          "He is horrified and disillusioned, beginning to consider desertion",
          "He remains completely loyal without any doubt"
        ],
        "a": 2,
        "e": "Enobarbus is horrified and disillusioned by the defeat, beginning to consider abandoning Antony's cause.",
        "h": "This reaction foreshadows Enobarbus's eventual desertion in Act IV.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra respond after fleeing the battle?",
        "o": [
          "She begs Antony's forgiveness for fleeing",
          "She blames Antony entirely for the defeat",
          "She refuses to speak to Antony",
          "She immediately surrenders to Caesar"
        ],
        "a": 0,
        "e": "Cleopatra begs Antony's forgiveness for fleeing the battle, and he forgives her.",
        "h": "Consider how Antony's love ultimately outweighs his reason here.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony's forgiveness of Cleopatra after Actium reveal?",
        "o": [
          "That he no longer loves her",
          "That his love outweighs his reason",
          "That he blames only himself",
          "That he has decided to leave Egypt forever"
        ],
        "a": 1,
        "e": "Antony's forgiveness reveals that his love for Cleopatra outweighs his reason, even after such a costly defeat.",
        "h": "This shows the continuing power of passion over judgement in Antony's character.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony request from Caesar after the defeat?",
        "o": [
          "A new alliance to attack Pompey",
          "Cleopatra's throne for himself",
          "Full pardon and restoration to power",
          "To be allowed to live privately in Egypt or Athens"
        ],
        "a": 3,
        "e": "Antony sends a message asking Caesar to be allowed to live privately in Egypt or Athens, but Caesar refuses.",
        "h": "Consider Antony's diminished ambitions after his defeat.",
        "yr": "SS3"
      },
      {
        "q": "Who does Caesar send to try to persuade Cleopatra to abandon Antony?",
        "o": [
          "Thidias",
          "Agrippa",
          "Mardian",
          "Maecenas"
        ],
        "a": 0,
        "e": "Caesar sends Thidias to try to persuade Cleopatra to abandon Antony.",
        "h": "This character provokes Antony's fury later in the act.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony do when he discovers Thidias with Cleopatra?",
        "o": [
          "He surrenders to Caesar",
          "He forgives Thidias immediately",
          "He kills Cleopatra",
          "He has Thidias whipped"
        ],
        "a": 3,
        "e": "Antony furiously has Thidias whipped after discovering him kissing Cleopatra's hand, showing his jealousy and declining self-control.",
        "h": "Consider Antony's emotional volatility in this scene.",
        "yr": "SS3"
      },
      {
        "q": "What does the whipping of Thidias reveal about Antony's state of mind?",
        "o": [
          "His jealousy and declining self-control",
          "His indifference to Cleopatra",
          "His growing trust in Caesar",
          "His complete emotional stability"
        ],
        "a": 0,
        "e": "The whipping of Thidias reveals Antony's jealousy and declining self-control as his situation worsens.",
        "h": "This event shows Antony's emotional state deteriorating under pressure.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Antony and Cleopatra – Acts IV & V",
    "topicCode": "SS3-LIT-05",
    "module": "Shakespearean Text — Antony and Cleopatra",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Acts IV and V bring the tragedy to its resolution: Enobarbus's death from guilt, Antony's final battles and suicide, and Cleopatra's own dignified death.\n</div>\n\n<h3 class=\"learn-subheading\">1. Enobarbus's Desertion and Death</h3>\n<p class=\"learn-p\">Having lost faith in Antony's judgement, <strong>Enobarbus deserts</strong> to Caesar's camp. Antony, learning of this, generously and without anger sends Enobarbus's belongings and treasure after him. Overwhelmed with guilt and shame at his own disloyalty in the face of such generosity, Enobarbus dies of a broken heart, calling out Antony's name — a symbol of the destructive cost of the Rome/Egypt conflict even on loyal men.</p>\n\n<h3 class=\"learn-subheading\">2. Antony's Temporary Land Victory</h3>\n<p class=\"learn-p\">On land, Antony achieves a brief victory against Caesar's forces and returns to Cleopatra triumphant and hopeful, praising his soldiers. This gives a short-lived rise in tension before the final catastrophe.</p>\n\n<h3 class=\"learn-subheading\">3. Final Defeat at Sea and Antony's Accusation</h3>\n<p class=\"learn-p\">In the final naval battle, Antony's Egyptian fleet surrenders to Caesar without a fight. Believing Cleopatra has betrayed him and sold him out to Caesar, Antony furiously denounces her as a \"triple-turned whore\" and vows revenge.</p>\n\n<h3 class=\"learn-subheading\">4. Cleopatra's False Death Message</h3>\n<p class=\"learn-p\">Terrified of Antony's rage, Cleopatra locks herself in her monument and sends word through her attendant <strong>Mardian</strong> that she has killed herself, her last word supposedly being \"Antony.\" She intended this to soften Antony's anger, but it produces a far more tragic result.</p>\n\n<h3 class=\"learn-subheading\">5. Antony's Suicide</h3>\n<p class=\"learn-p\">Devastated by the false news of Cleopatra's death, Antony asks his loyal servant <strong>Eros</strong> to kill him, but Eros kills himself instead rather than harm his master. Antony then falls on his own sword but fails to die instantly. He learns Cleopatra is alive and is carried, dying, to her monument, where he dies in her arms after urging her to seek safety with Caesar only on her own honourable terms.</p>\n\n<h3 class=\"learn-subheading\">6. Cleopatra's Meeting with Caesar</h3>\n<p class=\"learn-p\">Caesar treats Cleopatra with outward courtesy but secretly plans to parade her as a captive in his triumph in Rome — a humiliation Cleopatra is determined to avoid at all costs. Cleopatra tests Caesar's intentions and confirms, through his steward Seleucus's forced testimony about her hidden treasure, that Caesar cannot be trusted.</p>\n\n<h3 class=\"learn-subheading\">7. Cleopatra's Suicide</h3>\n<p class=\"learn-p\">Choosing death over dishonour, Cleopatra dresses in her royal robes and crown and applies poisonous asps (snakes) smuggled in a basket of figs to her arm and breast. Her attendants <strong>Charmian</strong> and <strong>Iras</strong> die alongside her. Charmian's final act is to straighten Cleopatra's crown before she too dies, declaring the death \"well done.\"</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Key Themes:</strong> Loyalty and betrayal (Enobarbus), the destructive power of jealousy (Antony's suicide), and dignity in death (Cleopatra choosing suicide over Roman humiliation) all converge in the tragic resolution.\n</div>\n",
    "questions": [
      {
        "q": "Why does Enobarbus desert Antony?",
        "o": [
          "He is ordered to by Caesar",
          "He has lost faith in Antony's judgement",
          "He wants to fight the Parthians",
          "Cleopatra banishes him"
        ],
        "a": 1,
        "e": "Enobarbus deserts Antony because he has lost faith in Antony's judgement after the disastrous defeat at Actium.",
        "h": "Consider Enobarbus's growing disillusionment shown in Act III.",
        "yr": "SS3"
      },
      {
        "q": "How does Antony react when he learns Enobarbus has deserted him?",
        "o": [
          "He ignores the desertion completely",
          "He blames Cleopatra for it",
          "He orders Enobarbus's execution",
          "He sends Enobarbus's belongings and treasure after him without anger"
        ],
        "a": 3,
        "e": "Antony generously and without anger sends Enobarbus's belongings and treasure after him.",
        "h": "Consider Antony's noble generosity even toward a deserter.",
        "yr": "SS3"
      },
      {
        "q": "What is Enobarbus's emotional reaction to Antony's generosity?",
        "o": [
          "Indifference",
          "Joy at receiving his treasure",
          "Overwhelming guilt and shame at his own disloyalty",
          "Anger at Antony"
        ],
        "a": 2,
        "e": "Enobarbus is overwhelmed with guilt and shame at his own disloyalty in the face of Antony's continued generosity.",
        "h": "This reaction leads directly to his death.",
        "yr": "SS3"
      },
      {
        "q": "How does Enobarbus die?",
        "o": [
          "By his own sword",
          "Poisoned by Cleopatra",
          "Of a broken heart, calling out Antony's name",
          "In battle against Caesar"
        ],
        "a": 2,
        "e": "Enobarbus dies of a broken heart, overwhelmed with guilt, calling out Antony's name.",
        "h": "Consider the emotional, not physical, cause of his death.",
        "yr": "SS3"
      },
      {
        "q": "What does Enobarbus's death symbolise?",
        "o": [
          "The triumph of reason over passion",
          "Caesar's cruelty",
          "Cleopatra's betrayal of Antony",
          "The destructive cost of the Rome/Egypt conflict even on loyal men"
        ],
        "a": 3,
        "e": "Enobarbus's death symbolises the destructive cost of the Rome/Egypt conflict, affecting even loyal men like him.",
        "h": "Consider the broader thematic significance of this death.",
        "yr": "SS3"
      },
      {
        "q": "What happens in Antony's brief land victory in Act IV?",
        "o": [
          "He is permanently defeated",
          "He achieves a temporary victory against Caesar's forces",
          "He captures Caesar himself",
          "He loses all his remaining soldiers"
        ],
        "a": 1,
        "e": "Antony achieves a brief, temporary victory against Caesar's forces on land, raising hope before the final catastrophe.",
        "h": "This victory is short-lived and precedes the final defeat.",
        "yr": "SS3"
      },
      {
        "q": "What happens to Antony's Egyptian fleet in the final naval battle?",
        "o": [
          "It never engages in battle",
          "It defeats Caesar's fleet decisively",
          "It surrenders to Caesar without a fight",
          "It is destroyed by a storm"
        ],
        "a": 2,
        "e": "In the final naval battle, Antony's Egyptian fleet surrenders to Caesar without a fight.",
        "h": "This betrayal deepens Antony's despair and suspicion.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony believe has happened when his fleet surrenders?",
        "o": [
          "That Pompey has intervened",
          "That Cleopatra has betrayed him and sold him out to Caesar",
          "That Caesar has died",
          "That his soldiers were simply too tired to fight"
        ],
        "a": 1,
        "e": "Antony believes Cleopatra has betrayed him and sold him out to Caesar when his fleet surrenders without a fight.",
        "h": "This belief fuels his fury toward Cleopatra.",
        "yr": "SS3"
      },
      {
        "q": "What insulting term does Antony use to denounce Cleopatra?",
        "o": [
          "'Loyal servant'",
          "'Noble empress'",
          "'Triple-turned whore'",
          "'Faithful queen'"
        ],
        "a": 2,
        "e": "Antony furiously denounces Cleopatra as a 'triple-turned whore,' believing she has betrayed him.",
        "h": "This harsh insult reflects Antony's rage and sense of betrayal.",
        "yr": "SS3"
      },
      {
        "q": "Where does Cleopatra hide after Antony's furious accusation?",
        "o": [
          "On a ship at sea",
          "In Caesar's camp",
          "In her monument",
          "In the Roman Senate"
        ],
        "a": 2,
        "e": "Terrified of Antony's rage, Cleopatra locks herself in her monument.",
        "h": "This location becomes the site of the play's final tragic scenes.",
        "yr": "SS3"
      },
      {
        "q": "What message does Cleopatra send to Antony through Mardian?",
        "o": [
          "That she has fled to Rome",
          "That she has joined Caesar",
          "That she forgives him",
          "That she has killed herself"
        ],
        "a": 3,
        "e": "Cleopatra sends word through Mardian that she has killed herself, with her last word supposedly being 'Antony.'",
        "h": "This false message is intended to soften Antony's anger.",
        "yr": "SS3"
      },
      {
        "q": "Why does Cleopatra send the false message of her death?",
        "o": [
          "To escape to Rome unnoticed",
          "To provoke Antony into attacking Caesar",
          "To soften Antony's anger toward her",
          "Because she genuinely plans to die soon after"
        ],
        "a": 2,
        "e": "Cleopatra sends the false death message intending to soften Antony's anger, but it produces a far more tragic result.",
        "h": "Consider her motive versus the actual outcome of this plan.",
        "yr": "SS3"
      },
      {
        "q": "Who does Antony ask to kill him after hearing of Cleopatra's supposed death?",
        "o": [
          "Enobarbus",
          "Caesar",
          "Eros",
          "Mardian"
        ],
        "a": 2,
        "e": "Antony asks his loyal servant Eros to kill him after hearing the false news of Cleopatra's death.",
        "h": "This servant makes a fatal choice of his own instead.",
        "yr": "SS3"
      },
      {
        "q": "What does Eros do instead of killing Antony?",
        "o": [
          "He refuses and does nothing",
          "He flees to Caesar's camp",
          "He kills Antony immediately",
          "He kills himself rather than harm his master"
        ],
        "a": 3,
        "e": "Eros kills himself rather than harm his master Antony, showing his deep loyalty.",
        "h": "Consider the extent of Eros's devotion to Antony.",
        "yr": "SS3"
      },
      {
        "q": "How does Antony attempt to end his own life?",
        "o": [
          "By poison",
          "By starvation",
          "By drowning",
          "By falling on his own sword"
        ],
        "a": 3,
        "e": "Antony falls on his own sword but fails to die instantly.",
        "h": "This method echoes the honourable Roman tradition of suicide.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony learn after wounding himself?",
        "o": [
          "That Caesar has been defeated",
          "That Cleopatra is actually alive",
          "That Enobarbus has returned",
          "That Octavia has died"
        ],
        "a": 1,
        "e": "Antony learns that Cleopatra is alive after wounding himself, prompting him to be carried to her monument.",
        "h": "This revelation comes too late to prevent his fatal wound.",
        "yr": "SS3"
      },
      {
        "q": "Where does Antony die?",
        "o": [
          "On the battlefield",
          "In Cleopatra's monument, in her arms",
          "At sea",
          "In Caesar's camp"
        ],
        "a": 1,
        "e": "Antony dies in Cleopatra's monument, in her arms, after being carried there while dying.",
        "h": "Consider the location of the play's central tragic reunion.",
        "yr": "SS3"
      },
      {
        "q": "What does Antony urge Cleopatra to do before he dies?",
        "o": [
          "Seek safety with Caesar only on her own honourable terms",
          "Flee to Parthia immediately",
          "Surrender unconditionally to Caesar",
          "Marry Caesar for protection"
        ],
        "a": 0,
        "e": "Antony urges Cleopatra to seek safety with Caesar only on her own honourable terms before he dies.",
        "h": "This shows Antony's concern for Cleopatra's dignity even in his final moments.",
        "yr": "SS3"
      },
      {
        "q": "How does Caesar treat Cleopatra after Antony's death?",
        "o": [
          "By marrying her himself",
          "With open hostility and immediate execution",
          "By releasing her from all obligations",
          "With outward courtesy but secret plans to humiliate her"
        ],
        "a": 3,
        "e": "Caesar treats Cleopatra with outward courtesy but secretly plans to parade her as a captive in his triumph in Rome.",
        "h": "Consider the gap between Caesar's public and private intentions.",
        "yr": "SS3"
      },
      {
        "q": "How does Cleopatra confirm that Caesar cannot be trusted?",
        "o": [
          "Through Antony's ghost",
          "Through a letter intercepted from Rome",
          "Through a direct confession from Caesar",
          "Through Seleucus's forced testimony about her hidden treasure"
        ],
        "a": 3,
        "e": "Cleopatra confirms Caesar's untrustworthiness through Seleucus's forced testimony about her hidden treasure during her meeting with Caesar.",
        "h": "This testimony exposes Caesar's true intentions toward Cleopatra.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Introduction to Unseen Prose",
    "topicCode": "SS3-LIT-06",
    "module": "Literary Appreciation and Exam Skills",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  In the WASSCE Literature-in-English paper, candidates are given a short passage of prose they have never seen before (\"unseen prose\") and asked questions testing comprehension, analysis, and appreciation — without relying on memorised knowledge of a set text.\n</div>\n\n<h3 class=\"learn-subheading\">1. What Unseen Prose Tests</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Skill</th><th>What It Involves</th></tr></thead>\n    <tbody>\n      <tr><td>Comprehension</td><td>Understanding what literally happens in the passage.</td></tr>\n      <tr><td>Plot analysis</td><td>Identifying the sequence and significance of events.</td></tr>\n      <tr><td>Character analysis</td><td>Inferring a character's traits from their words, actions, and thoughts.</td></tr>\n      <tr><td>Setting</td><td>Identifying where and when the extract takes place, and its effect.</td></tr>\n      <tr><td>Tone and mood</td><td>The writer's attitude and the atmosphere created for the reader.</td></tr>\n      <tr><td>Style and language</td><td>Word choice, sentence structure, and figurative language used.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Strategy for Approaching Unseen Prose</h3>\n<ol style=\"margin-left:20px;\">\n  <li>Read the passage through once fully, without stopping, to grasp the general sense.</li>\n  <li>Read the questions carefully before re-reading the passage a second time.</li>\n  <li>Underline or note key words, phrases, and details relevant to the questions.</li>\n  <li>Answer using evidence from the passage — avoid inventing details not supported by the text.</li>\n  <li>Support points with brief, relevant references to the passage rather than long quotations.</li>\n</ol>\n\n<h3 class=\"learn-subheading\">3. Common Question Types</h3>\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  Typical unseen prose questions ask candidates to: summarise a section in their own words; explain the meaning of specific words/phrases as used in context; describe a character's feelings or motivation; identify and explain a literary device (e.g., simile, irony, flashback); or comment on the effectiveness of the writer's technique.\n</div>\n\n<h3 class=\"learn-subheading\">4. Avoiding Common Mistakes</h3>\n<p class=\"learn-p\">Candidates often lose marks by: answering from memory of other stories instead of the given passage; copying long, unexplained quotations instead of analysing them; misreading the question's specific instruction (e.g., \"in your own words\"); and giving an answer that is too general to earn marks tied to the passage's specific content.</p>\n",
    "questions": [
      {
        "q": "What does unseen prose testing primarily assess?",
        "o": [
          "Comprehension, analysis, and appreciation of a new passage",
          "Memorised knowledge of a set text",
          "Knowledge of Roman history",
          "Ability to recite poetry from memory"
        ],
        "a": 0,
        "e": "Unseen prose tests comprehension, analysis, and appreciation of a passage the candidate has never seen before, without relying on memorised knowledge.",
        "h": "Consider the whole point of using a passage never studied before.",
        "yr": "SS3"
      },
      {
        "q": "What does 'comprehension' involve when studying unseen prose?",
        "o": [
          "Understanding what literally happens in the passage",
          "Comparing it to a set text",
          "Memorising the passage word for word",
          "Ignoring the passage's content entirely"
        ],
        "a": 0,
        "e": "Comprehension involves understanding what literally happens in the passage.",
        "h": "This is the most basic reading skill tested.",
        "yr": "SS3"
      },
      {
        "q": "What does 'plot analysis' involve in unseen prose?",
        "o": [
          "Ignoring the order of events",
          "Counting the number of characters",
          "Memorising exact quotations",
          "Identifying the sequence and significance of events"
        ],
        "a": 3,
        "e": "Plot analysis involves identifying the sequence and significance of events in the passage.",
        "h": "Consider how events unfold and why they matter.",
        "yr": "SS3"
      },
      {
        "q": "How is character analysis approached in unseen prose?",
        "o": [
          "By only considering a character's name",
          "By inferring traits from words, actions, and thoughts",
          "By ignoring what characters say or do",
          "By assuming all characters are the same"
        ],
        "a": 1,
        "e": "Character analysis in unseen prose involves inferring a character's traits from their words, actions, and thoughts.",
        "h": "Consider how a reader builds an impression of a character.",
        "yr": "SS3"
      },
      {
        "q": "What does examining 'setting' involve in an unseen prose passage?",
        "o": [
          "Ignoring the location entirely",
          "Only focusing on character dialogue",
          "Memorising historical dates",
          "Identifying where and when the extract takes place, and its effect"
        ],
        "a": 3,
        "e": "Examining setting involves identifying where and when the extract takes place, and considering its effect on the passage.",
        "h": "Setting includes both place and time.",
        "yr": "SS3"
      },
      {
        "q": "What is the difference between 'tone' and 'mood' in a passage?",
        "o": [
          "They mean exactly the same thing",
          "Tone refers only to setting; mood refers only to characters",
          "Neither concept applies to prose",
          "Tone is the writer's attitude; mood is the atmosphere created for the reader"
        ],
        "a": 3,
        "e": "Tone refers to the writer's attitude, while mood refers to the atmosphere created for the reader.",
        "h": "Consider the distinction between the writer's stance and the reader's feeling.",
        "yr": "SS3"
      },
      {
        "q": "What does 'style and language' analysis involve?",
        "o": [
          "Word choice, sentence structure, and figurative language",
          "Only counting the number of paragraphs",
          "Memorising the author's biography",
          "Ignoring how the passage is written"
        ],
        "a": 0,
        "e": "Style and language analysis involves examining word choice, sentence structure, and figurative language used by the writer.",
        "h": "This concerns how something is said, not just what is said.",
        "yr": "SS3"
      },
      {
        "q": "What should a candidate do first when approaching an unseen prose passage?",
        "o": [
          "Skip the passage and guess the answers",
          "Memorise the passage word for word",
          "Answer the questions immediately without reading",
          "Read the passage through once fully without stopping"
        ],
        "a": 3,
        "e": "Candidates should read the passage through once fully, without stopping, to grasp the general sense first.",
        "h": "This initial read establishes overall understanding before detailed analysis.",
        "yr": "SS3"
      },
      {
        "q": "What should a candidate do after the first reading of an unseen passage?",
        "o": [
          "Read the questions carefully before re-reading the passage",
          "Skip to writing without re-reading",
          "Submit answers immediately",
          "Ignore the questions and write a summary"
        ],
        "a": 0,
        "e": "After the first reading, candidates should read the questions carefully before re-reading the passage a second time.",
        "h": "This step ensures answers focus on what is actually being asked.",
        "yr": "SS3"
      },
      {
        "q": "Why should candidates underline or note key details while reading unseen prose?",
        "o": [
          "To avoid reading the passage at all",
          "Because it is a required formality with no real purpose",
          "To identify details relevant to the specific questions asked",
          "Because underlining guarantees full marks"
        ],
        "a": 2,
        "e": "Underlining or noting key words, phrases, and details helps candidates find information relevant to the questions being asked.",
        "h": "This is a practical strategy to organise a candidate's response.",
        "yr": "SS3"
      },
      {
        "q": "What should answers to unseen prose questions be based on?",
        "o": [
          "General assumptions with no textual support",
          "A different text entirely",
          "Evidence from the passage, not invented details",
          "The candidate's imagination alone"
        ],
        "a": 2,
        "e": "Answers should use evidence from the passage — candidates should avoid inventing details not supported by the text.",
        "h": "Consider the importance of staying grounded in the actual passage.",
        "yr": "SS3"
      },
      {
        "q": "How should candidates support points in their answers?",
        "o": [
          "By copying the entire passage",
          "With extremely long, unexplained quotations",
          "Without any reference to the passage at all",
          "With brief, relevant references to the passage rather than long quotations"
        ],
        "a": 3,
        "e": "Candidates should support points with brief, relevant references to the passage rather than long, unexplained quotations.",
        "h": "Consider the balance between using evidence and over-quoting.",
        "yr": "SS3"
      },
      {
        "q": "What kind of question might ask candidates to explain a word's meaning?",
        "o": [
          "A question asking for a translation into another language",
          "A question about unrelated set texts",
          "A question asking to explain the meaning of a word/phrase as used in context",
          "A question about the author's date of birth"
        ],
        "a": 2,
        "e": "Unseen prose questions often ask candidates to explain the meaning of specific words or phrases as used in context.",
        "h": "This tests vocabulary understanding within the specific passage.",
        "yr": "SS3"
      },
      {
        "q": "What might a question about a character's motivation require?",
        "o": [
          "Comparing the character to a real historical figure",
          "Ignoring the character entirely",
          "Listing unrelated character names",
          "Describing a character's feelings or motivation based on the passage"
        ],
        "a": 3,
        "e": "Such questions require candidates to describe a character's feelings or motivation, based on evidence from the passage.",
        "h": "This tests inferential understanding of character psychology.",
        "yr": "SS3"
      },
      {
        "q": "What type of question asks about literary devices in unseen prose?",
        "o": [
          "A question identifying and explaining a device like simile or irony",
          "A question about grammar rules unrelated to the passage",
          "A question asking only for a summary",
          "A question about spelling"
        ],
        "a": 0,
        "e": "Some questions ask candidates to identify and explain a literary device, such as simile, irony, or flashback.",
        "h": "This tests a candidate's ability to recognise and analyse technique.",
        "yr": "SS3"
      },
      {
        "q": "What common mistake involves answering from memory of other stories?",
        "o": [
          "Reading the passage too many times",
          "Using brief references to the passage",
          "Answering from memory of other stories instead of the given passage",
          "Underlining too many details"
        ],
        "a": 2,
        "e": "A common mistake is answering from memory of other stories instead of using the specific given passage.",
        "h": "Consider the importance of focusing only on the actual text provided.",
        "yr": "SS3"
      },
      {
        "q": "What common mistake involves copying long quotations?",
        "o": [
          "Using quotations only when relevant",
          "Copying long, unexplained quotations instead of analysing them",
          "Avoiding quotations altogether",
          "Explaining every quotation briefly and clearly"
        ],
        "a": 1,
        "e": "A common mistake is copying long, unexplained quotations instead of properly analysing them.",
        "h": "Consider the difference between quoting and actually interpreting a text.",
        "yr": "SS3"
      },
      {
        "q": "What common mistake involves misreading a question's specific instruction?",
        "o": [
          "Misreading an instruction such as 'in your own words'",
          "Underlining relevant details",
          "Reading every instruction carefully",
          "Writing answers that are too specific"
        ],
        "a": 0,
        "e": "A common mistake is misreading the question's specific instruction, such as failing to answer 'in your own words' as required.",
        "h": "Consider how failing to follow specific instructions can cost marks.",
        "yr": "SS3"
      },
      {
        "q": "Why does giving a general, non-specific answer lose marks?",
        "o": [
          "Because general answers are always preferred",
          "Because unseen prose has no specific content",
          "Because examiners never read the passage",
          "Because marks are tied to the passage's specific content"
        ],
        "a": 3,
        "e": "A general answer that isn't tied to the passage's specific content loses marks, since examiners are marking based on the given text.",
        "h": "Consider why specificity to the passage matters for scoring.",
        "yr": "SS3"
      },
      {
        "q": "What overall skill does the unseen prose section test that a candidate cannot prepare for by memorising a set text?",
        "o": [
          "Knowledge of the poet's biography",
          "The ability to independently comprehend and analyse a new passage",
          "Knowledge of Roman history",
          "The ability to recite the set texts from memory"
        ],
        "a": 1,
        "e": "Unseen prose specifically tests the ability to independently comprehend and analyse a passage never studied before, unlike questions on memorised set texts.",
        "h": "This is the defining purpose of the unseen prose section.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Introduction to Unseen Poem",
    "topicCode": "SS3-LIT-07",
    "module": "Literary Appreciation and Exam Skills",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Alongside unseen prose, candidates are also given a short poem they have not studied before, and are tested on their ability to interpret meaning, structure, and poetic devices independently.\n</div>\n\n<h3 class=\"learn-subheading\">1. Features to Examine in an Unseen Poem</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>What to Look For</th></tr></thead>\n    <tbody>\n      <tr><td>Subject matter</td><td>What the poem is literally about, on the surface.</td></tr>\n      <tr><td>Theme</td><td>The deeper message or idea the poet is exploring.</td></tr>\n      <tr><td>Structure</td><td>Stanza pattern, line length, rhyme scheme, and how form relates to meaning.</td></tr>\n      <tr><td>Imagery</td><td>Descriptive, sensory language that creates pictures in the reader's mind.</td></tr>\n      <tr><td>Mood/tone</td><td>The emotional atmosphere and the speaker's attitude.</td></tr>\n      <tr><td>Sound devices</td><td>Rhyme, rhythm, alliteration, and assonance, and their effect.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Common Literary Devices in Unseen Poems</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Device</th><th>Definition</th></tr></thead>\n    <tbody>\n      <tr><td>Metaphor</td><td>A direct comparison between two unlike things without using \"like\" or \"as\".</td></tr>\n      <tr><td>Simile</td><td>A comparison using \"like\" or \"as\".</td></tr>\n      <tr><td>Personification</td><td>Giving human qualities to non-human things or ideas.</td></tr>\n      <tr><td>Irony</td><td>A contrast between expectation and reality, or between what is said and what is meant.</td></tr>\n      <tr><td>Alliteration</td><td>Repetition of the same initial consonant sound in nearby words.</td></tr>\n      <tr><td>Paradox</td><td>A statement that seems self-contradictory but reveals a deeper truth.</td></tr>\n      <tr><td>Symbolism</td><td>Using an object or image to represent a larger idea.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Strategy for Approaching an Unseen Poem</h3>\n<ol style=\"margin-left:20px;\">\n  <li>Read the poem slowly, at least twice — poems reward re-reading more than prose does.</li>\n  <li>Identify the speaker (persona) — who is \"speaking\" the poem, and to whom?</li>\n  <li>Note the title, since it often gives a clue to the theme.</li>\n  <li>Look for the \"turn\" — a shift in tone, focus, or argument, often near the end.</li>\n  <li>Always explain the effect of a device you identify, not just name it.</li>\n</ol>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Never just say \"the poet uses a simile.\" Say what is being compared, and explain what effect or meaning that comparison creates — examiners award marks for interpretation, not just identification.\n</div>\n",
    "questions": [
      {
        "q": "What does 'subject matter' refer to in an unseen poem?",
        "o": [
          "The poem's publication date",
          "What the poem is literally about, on the surface",
          "The rhyme scheme only",
          "The poet's private life"
        ],
        "a": 1,
        "e": "Subject matter refers to what the poem is literally about, on the surface.",
        "h": "This is the most basic level of understanding a poem.",
        "yr": "SS3"
      },
      {
        "q": "What is the difference between 'subject matter' and 'theme'?",
        "o": [
          "Subject matter is literal content; theme is the deeper message or idea",
          "They are exactly the same thing",
          "Theme refers only to rhyme; subject matter refers only to imagery",
          "Neither concept applies to poetry"
        ],
        "a": 0,
        "e": "Subject matter is what the poem is literally about, while theme is the deeper message or idea the poet explores.",
        "h": "Consider the difference between surface content and underlying meaning.",
        "yr": "SS3"
      },
      {
        "q": "What does examining 'structure' in a poem involve?",
        "o": [
          "Stanza pattern, line length, rhyme scheme, and how form relates to meaning",
          "Memorising the poet's biography",
          "Ignoring the poem's form entirely",
          "Only counting the number of words"
        ],
        "a": 0,
        "e": "Structure involves examining stanza pattern, line length, rhyme scheme, and how the poem's form relates to its meaning.",
        "h": "This concerns the poem's organisation and shape.",
        "yr": "SS3"
      },
      {
        "q": "What is 'imagery' in poetry?",
        "o": [
          "A list of characters",
          "A type of rhyme scheme",
          "Descriptive, sensory language that creates pictures in the reader's mind",
          "The poem's title only"
        ],
        "a": 2,
        "e": "Imagery is descriptive, sensory language that creates pictures in the reader's mind.",
        "h": "This device appeals to the senses of sight, sound, touch, taste, or smell.",
        "yr": "SS3"
      },
      {
        "q": "What is the difference between 'mood' and 'tone' when discussing a poem?",
        "o": [
          "Mood is the emotional atmosphere; tone is the speaker's attitude",
          "Neither concept applies to poetry",
          "They are identical concepts with no difference",
          "Mood refers only to rhyme; tone refers only to imagery"
        ],
        "a": 0,
        "e": "Mood is the emotional atmosphere of the poem, while tone is the speaker's attitude toward the subject.",
        "h": "Consider the distinction between the overall feeling and the speaker's stance.",
        "yr": "SS3"
      },
      {
        "q": "What are 'sound devices' in poetry?",
        "o": [
          "Rhyme, rhythm, alliteration, and assonance, and their effect",
          "Only the poem's title",
          "The poet's publication history",
          "A summary of the poem's plot"
        ],
        "a": 0,
        "e": "Sound devices include rhyme, rhythm, alliteration, and assonance, along with their effect on the reader.",
        "h": "These devices concern how a poem sounds when read aloud.",
        "yr": "SS3"
      },
      {
        "q": "What is a metaphor?",
        "o": [
          "Giving human qualities to non-human things",
          "Repetition of consonant sounds",
          "A direct comparison between two unlike things without using 'like' or 'as'",
          "A comparison using 'like' or 'as'"
        ],
        "a": 2,
        "e": "A metaphor is a direct comparison between two unlike things without using 'like' or 'as'.",
        "h": "Distinguish this from a simile, which uses comparison words.",
        "yr": "SS3"
      },
      {
        "q": "What is a simile?",
        "o": [
          "A statement that contradicts itself",
          "A comparison using 'like' or 'as'",
          "Repetition of initial consonant sounds",
          "A direct comparison without comparison words"
        ],
        "a": 1,
        "e": "A simile is a comparison that uses the words 'like' or 'as'.",
        "h": "This device explicitly signals a comparison with a specific word.",
        "yr": "SS3"
      },
      {
        "q": "What is personification?",
        "o": [
          "Giving human qualities to non-human things or ideas",
          "A comparison using 'like' or 'as'",
          "Repetition of vowel sounds",
          "A statement that seems self-contradictory"
        ],
        "a": 0,
        "e": "Personification is giving human qualities to non-human things or ideas.",
        "h": "Consider when an object or idea is described as if it were human.",
        "yr": "SS3"
      },
      {
        "q": "What is irony in poetry?",
        "o": [
          "A direct comparison between two things",
          "A description using only positive language",
          "The repetition of a single sound",
          "A contrast between expectation and reality, or between what is said and meant"
        ],
        "a": 3,
        "e": "Irony is a contrast between expectation and reality, or between what is said and what is actually meant.",
        "h": "This device often involves an unexpected twist or gap in meaning.",
        "yr": "SS3"
      },
      {
        "q": "What is alliteration?",
        "o": [
          "Giving human qualities to objects",
          "A statement that contradicts itself",
          "A comparison using 'like' or 'as'",
          "Repetition of the same initial consonant sound in nearby words"
        ],
        "a": 3,
        "e": "Alliteration is the repetition of the same initial consonant sound in nearby words.",
        "h": "This is a sound device focused on the beginnings of words.",
        "yr": "SS3"
      },
      {
        "q": "What is a paradox?",
        "o": [
          "A repetition of consonant sounds",
          "A poem with no stanzas",
          "A statement that seems self-contradictory but reveals a deeper truth",
          "A comparison using 'like' or 'as'"
        ],
        "a": 2,
        "e": "A paradox is a statement that seems self-contradictory on the surface but reveals a deeper truth.",
        "h": "This device initially confuses the reader before revealing insight.",
        "yr": "SS3"
      },
      {
        "q": "What is symbolism in poetry?",
        "o": [
          "A type of rhyme scheme",
          "The poem's title only",
          "A direct statement with no hidden meaning",
          "Using an object or image to represent a larger idea"
        ],
        "a": 3,
        "e": "Symbolism is the use of an object or image to represent a larger idea beyond its literal meaning.",
        "h": "This device gives concrete objects abstract significance.",
        "yr": "SS3"
      },
      {
        "q": "What is the first step recommended for approaching an unseen poem?",
        "o": [
          "Answering the questions before reading the poem",
          "Reading the poem slowly, at least twice",
          "Memorising the poem word for word",
          "Skipping the poem and guessing"
        ],
        "a": 1,
        "e": "The first step is reading the poem slowly, at least twice, since poems reward re-reading more than prose does.",
        "h": "Poetry's density means it benefits from repeated reading.",
        "yr": "SS3"
      },
      {
        "q": "Why is identifying the 'speaker' (persona) important in an unseen poem?",
        "o": [
          "Because the speaker never matters to interpretation",
          "To understand who is 'speaking' the poem, and to whom",
          "Because the speaker is always the poet themselves",
          "Because only poems with named speakers can be analysed"
        ],
        "a": 1,
        "e": "Identifying the speaker (persona) helps determine who is 'speaking' the poem and to whom, which is key to interpretation.",
        "h": "The speaker may or may not be the poet themselves.",
        "yr": "SS3"
      },
      {
        "q": "Why is the title of a poem significant?",
        "o": [
          "It has no relevance to interpretation",
          "It is always identical to the poem's first line",
          "It only matters for very long poems",
          "It often gives a clue to the theme"
        ],
        "a": 3,
        "e": "The title often gives a clue to the poem's theme, making it worth careful consideration.",
        "h": "Titles are often chosen deliberately to hint at meaning.",
        "yr": "SS3"
      },
      {
        "q": "What is meant by looking for the 'turn' in a poem?",
        "o": [
          "A shift in tone, focus, or argument, often near the end",
          "A change in the poet's handwriting",
          "The physical act of turning the page",
          "The number of stanzas in the poem"
        ],
        "a": 0,
        "e": "The 'turn' refers to a shift in tone, focus, or argument within the poem, often occurring near the end.",
        "h": "This shift can significantly affect the poem's overall meaning.",
        "yr": "SS3"
      },
      {
        "q": "What should a candidate do when identifying a literary device in a poem?",
        "o": [
          "Simply name the device without further comment",
          "List every possible device regardless of relevance",
          "Explain the effect of the device, not just name it",
          "Ignore any devices found"
        ],
        "a": 2,
        "e": "Candidates should always explain the effect of a device they identify, not just name it, since examiners award marks for interpretation.",
        "h": "Simply naming a device without explanation earns limited credit.",
        "yr": "SS3"
      },
      {
        "q": "Why do examiners award more marks for explaining a device's effect than for merely naming it?",
        "o": [
          "Because interpretation, not just identification, demonstrates true understanding",
          "Because devices have no effect on meaning",
          "Because examiners prefer longer answers regardless of content",
          "Because naming a device is always sufficient"
        ],
        "a": 0,
        "e": "Examiners award more marks for interpretation because explaining a device's effect demonstrates true understanding, not just recognition.",
        "h": "This reflects the deeper analytical skill being tested.",
        "yr": "SS3"
      },
      {
        "q": "Overall, what makes unseen poetry analysis different from analysing a memorised poem?",
        "o": [
          "It is identical to analysing unseen prose in every way",
          "It tests independent interpretation skills without relying on prior study",
          "It requires no reading of the poem at all",
          "It only tests memory of rhyme schemes"
        ],
        "a": 1,
        "e": "Unseen poetry analysis tests independent interpretation skills, since candidates cannot rely on prior study or memorisation of the poem.",
        "h": "This is the defining purpose of the unseen poem section.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Principles of Answering Context and Literature Questions",
    "topicCode": "SS3-LIT-08",
    "module": "Literary Appreciation and Exam Skills",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  WASSCE Literature-in-English questions often use specific instructional verbs. Understanding exactly what each verb demands is essential to scoring well, especially in context questions drawn directly from set texts.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning of a Context Question</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">context question</span> presents a short extract or passage taken directly from a set text (prose, drama, or poetry) and asks the candidate to answer questions based on the surrounding circumstances — who is speaking, to whom, where, and why — as well as the passage's meaning and significance.</p>\n\n<h3 class=\"learn-subheading\">2. Common Instructional (Exam) Verbs</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Verb</th><th>What It Requires</th></tr></thead>\n    <tbody>\n      <tr><td>Comment on</td><td>Give a reasoned opinion or observation, supported with evidence.</td></tr>\n      <tr><td>Describe</td><td>Give a detailed, accurate account of something.</td></tr>\n      <tr><td>Contrast</td><td>Point out the differences between two things.</td></tr>\n      <tr><td>Compare</td><td>Point out similarities (and often differences) between two things.</td></tr>\n      <tr><td>Examine</td><td>Investigate a subject in detail, considering different aspects.</td></tr>\n      <tr><td>Discuss</td><td>Explore a topic from multiple angles, weighing points for and against.</td></tr>\n      <tr><td>Justify</td><td>Give reasons or evidence to support a claim or judgement.</td></tr>\n      <tr><td>Illustrate</td><td>Use examples to make a point clear.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Structure for a Context Question Answer</h3>\n<ol style=\"margin-left:20px;\">\n  <li><strong>Context:</strong> Identify the speaker, the listener, and the situation.</li>\n  <li><strong>Meaning:</strong> Paraphrase or explain what is being said in the extract.</li>\n  <li><strong>Significance:</strong> Explain the extract's importance to plot, character, or theme.</li>\n  <li><strong>Device/Style (if asked):</strong> Identify any notable literary device and its effect.</li>\n</ol>\n\n<h3 class=\"learn-subheading\">4. Developing Accurate, Concise Responses</h3>\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  Concise answers that directly address the exact verb used score higher than long, vague answers. If a question says \"contrast,\" simply describing both things without stating their differences will lose marks — always answer the verb, not just the topic.\n</div>\n\n<h3 class=\"learn-subheading\">5. Common Errors to Avoid</h3>\n<p class=\"learn-p\">Avoid: retelling the whole plot instead of answering the specific question asked; ignoring the instructional verb (e.g., writing a description when asked to \"justify\"); failing to use the extract itself as evidence; and writing answers with no clear structure or organisation.</p>\n",
    "questions": [
      {
        "q": "What is a context question in WASSCE Literature-in-English?",
        "o": [
          "A question with no connection to any set text",
          "A question only about grammar",
          "A short extract from a set text with questions on its circumstances and meaning",
          "A question about the examiner's personal opinion"
        ],
        "a": 2,
        "e": "A context question presents a short extract taken directly from a set text and asks questions based on the surrounding circumstances and meaning.",
        "h": "This question type is drawn directly from studied texts.",
        "yr": "SS3"
      },
      {
        "q": "What information does a context question typically require about the extract?",
        "o": [
          "Who is speaking, to whom, where, and why",
          "The exact date the text was published",
          "Only the page number of the extract",
          "The examiner's personal interpretation"
        ],
        "a": 0,
        "e": "A context question typically requires identifying who is speaking, to whom, where, and why.",
        "h": "This forms the foundation of understanding any extract's context.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'comment on' require?",
        "o": [
          "Listing unrelated facts",
          "Giving a reasoned opinion or observation, supported with evidence",
          "Ignoring the question entirely",
          "Simply repeating the passage word for word"
        ],
        "a": 1,
        "e": "'Comment on' requires giving a reasoned opinion or observation, supported with evidence from the text.",
        "h": "This verb asks for thoughtful, evidence-based reflection.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'describe' require?",
        "o": [
          "Comparing two unrelated texts",
          "Giving a detailed, accurate account of something",
          "Ignoring specific details",
          "Giving only a one-word answer"
        ],
        "a": 1,
        "e": "'Describe' requires giving a detailed, accurate account of something.",
        "h": "This verb calls for thorough, precise description.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'contrast' require?",
        "o": [
          "Describing only one thing in isolation",
          "Ignoring both things entirely",
          "Pointing out the differences between two things",
          "Pointing out only similarities between two things"
        ],
        "a": 2,
        "e": "'Contrast' requires pointing out the differences between two things.",
        "h": "This verb specifically asks for points of difference, not similarity.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'compare' require?",
        "o": [
          "Ignoring any connection between two things",
          "Discussing only one item in detail",
          "Pointing out only differences with no similarities",
          "Pointing out similarities (and often differences) between two things"
        ],
        "a": 3,
        "e": "'Compare' requires pointing out similarities, and often differences, between two things.",
        "h": "This verb is broader than 'contrast,' which focuses only on differences.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'examine' require?",
        "o": [
          "Comparing unrelated topics",
          "Investigating a subject in detail, considering different aspects",
          "Giving a brief, one-sentence answer",
          "Ignoring the subject's complexity"
        ],
        "a": 1,
        "e": "'Examine' requires investigating a subject in detail, considering its different aspects.",
        "h": "This verb calls for a thorough, multi-angled investigation.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'discuss' require?",
        "o": [
          "Simply summarising the plot",
          "Exploring a topic from multiple angles, weighing points for and against",
          "Ignoring opposing viewpoints entirely",
          "Giving a single, unsupported opinion"
        ],
        "a": 1,
        "e": "'Discuss' requires exploring a topic from multiple angles, weighing points for and against.",
        "h": "This verb requires balanced consideration of different perspectives.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'justify' require?",
        "o": [
          "Simply restating the question",
          "Describing an unrelated topic",
          "Giving reasons or evidence to support a claim or judgement",
          "Ignoring all evidence"
        ],
        "a": 2,
        "e": "'Justify' requires giving reasons or evidence to support a claim or judgement.",
        "h": "This verb demands clear supporting reasoning.",
        "yr": "SS3"
      },
      {
        "q": "What does the instructional verb 'illustrate' require?",
        "o": [
          "Avoiding all examples",
          "Giving a vague, general statement",
          "Using examples to make a point clear",
          "Ignoring the question's focus"
        ],
        "a": 2,
        "e": "'Illustrate' requires using examples to make a point clear.",
        "h": "This verb calls for concrete evidence to support an argument.",
        "yr": "SS3"
      },
      {
        "q": "What is the first step in structuring a context question answer?",
        "o": [
          "Identifying the speaker, the listener, and the situation",
          "Listing every character in the play",
          "Explaining the deeper theme immediately",
          "Giving a personal opinion about the text"
        ],
        "a": 0,
        "e": "The first step is identifying the speaker, the listener, and the situation — establishing the context of the extract.",
        "h": "This lays the foundation before further analysis.",
        "yr": "SS3"
      },
      {
        "q": "What is the second recommended step in a context question answer?",
        "o": [
          "Comparing the extract to an unrelated text",
          "Writing a full plot summary of the text",
          "Identifying the speaker only",
          "Paraphrasing or explaining what is being said in the extract"
        ],
        "a": 3,
        "e": "The second step is paraphrasing or explaining the meaning of what is being said in the extract.",
        "h": "This step focuses on clarifying the content of the passage.",
        "yr": "SS3"
      },
      {
        "q": "What is the third recommended step in a context question answer?",
        "o": [
          "Ignoring the extract's broader importance",
          "Repeating the first step",
          "Explaining the extract's significance to plot, character, or theme",
          "Listing unrelated quotations"
        ],
        "a": 2,
        "e": "The third step is explaining the extract's significance to plot, character, or theme.",
        "h": "This step connects the extract to the wider text.",
        "yr": "SS3"
      },
      {
        "q": "When should a candidate identify a literary device in a context answer?",
        "o": [
          "When specifically asked to address device or style",
          "In every single answer regardless of the question",
          "Only when discussing unseen poems",
          "Never, as devices are irrelevant to context questions"
        ],
        "a": 0,
        "e": "A candidate should identify a literary device and its effect when specifically asked to address device or style.",
        "h": "This step is conditional on what the question actually asks.",
        "yr": "SS3"
      },
      {
        "q": "Why do concise answers that directly address the instructional verb score higher?",
        "o": [
          "Because examiners prefer the shortest possible answers regardless of content",
          "Because instructional verbs are irrelevant to scoring",
          "Because vague, general answers do not properly answer the specific question asked",
          "Because long answers are always penalised"
        ],
        "a": 2,
        "e": "Concise answers that directly address the instructional verb score higher because they properly respond to the specific question asked.",
        "h": "This reflects the importance of precisely following exam instructions.",
        "yr": "SS3"
      },
      {
        "q": "What happens if a question says 'contrast' but a candidate only describes both things without stating differences?",
        "o": [
          "There is no penalty for this approach",
          "The examiner ignores the instructional verb entirely",
          "The candidate automatically receives full marks",
          "The candidate loses marks for not answering the specific verb"
        ],
        "a": 3,
        "e": "If a question says 'contrast' but the candidate only describes both things without stating differences, marks are lost for not answering the specific verb.",
        "h": "This illustrates why understanding instructional verbs precisely matters.",
        "yr": "SS3"
      },
      {
        "q": "What common error involves retelling the whole plot?",
        "o": [
          "Answering only the specific question with no extra detail",
          "Retelling the whole plot instead of answering the specific question asked",
          "Following the instructional verb precisely",
          "Using the extract as the main evidence"
        ],
        "a": 1,
        "e": "A common error is retelling the whole plot instead of answering the specific question asked.",
        "h": "This wastes time and fails to address the actual question.",
        "yr": "SS3"
      },
      {
        "q": "What common error involves ignoring the instructional verb?",
        "o": [
          "Writing a description when asked to 'justify', for example",
          "Using evidence from the extract",
          "Structuring the answer clearly",
          "Following the instructional verb precisely"
        ],
        "a": 0,
        "e": "A common error is ignoring the instructional verb, such as writing a description when asked to 'justify.'",
        "h": "This mismatch between the verb and the answer type costs marks.",
        "yr": "SS3"
      },
      {
        "q": "What common error involves not using the extract itself?",
        "o": [
          "Failing to use the extract as evidence in the answer",
          "Structuring an answer around the extract's content",
          "Quoting the extract too frequently",
          "Using only brief references to the extract"
        ],
        "a": 0,
        "e": "A common error is failing to use the extract itself as evidence to support the answer.",
        "h": "The extract should ground and support every context answer.",
        "yr": "SS3"
      },
      {
        "q": "What common error involves lack of organisation?",
        "o": [
          "Identifying the speaker and listener clearly",
          "Writing answers with no clear structure or organisation",
          "Following the four-step structure recommended for context answers",
          "Explaining the extract's significance"
        ],
        "a": 1,
        "e": "A common error is writing answers with no clear structure or organisation, making the answer harder to follow and mark.",
        "h": "A clear structure helps both the candidate and the examiner.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Review of Shakespearean Text (Antony and Cleopatra)",
    "topicCode": "SS3-LIT-09",
    "module": "Second Term Review",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic consolidates the whole of <strong>Antony and Cleopatra</strong>, drawing together characterisation, plot development, major conflicts, and the moral lessons the play offers to a modern audience.\n</div>\n\n<h3 class=\"learn-subheading\">1. Characterisation Review</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Character</th><th>Defining Trait</th></tr></thead>\n    <tbody>\n      <tr><td>Antony</td><td>A once-great soldier undone by passion; torn between duty and love; ultimately noble in his final loyalty to Cleopatra.</td></tr>\n      <tr><td>Cleopatra</td><td>Complex, theatrical, and politically astute; capable of genuine love but also of manipulation; dies with dignity.</td></tr>\n      <tr><td>Octavius Caesar</td><td>Calculating, ambitious, and self-controlled; represents cold political reason over passion.</td></tr>\n      <tr><td>Enobarbus</td><td>Loyal but pragmatic; his desertion and death show the tragic cost of choosing self-preservation over loyalty.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Plot Development — The Arc of the Tragedy</h3>\n<p class=\"learn-p\">The play moves from Antony's initial neglect of Rome for Egypt (Act I), through his temporary reconciliation with Caesar via marriage to Octavia (Act II), to the catastrophic choice to follow Cleopatra at Actium (Act III), and finally to the deaths of Enobarbus, Antony, and Cleopatra (Acts IV–V). Each act deepens the central conflict between reason/duty (Rome) and passion/pleasure (Egypt).</p>\n\n<h3 class=\"learn-subheading\">3. Major Conflicts</h3>\n<ul style=\"margin-left:20px;\">\n  <li><strong>Internal conflict:</strong> Antony's struggle between his Roman identity and his love for Cleopatra.</li>\n  <li><strong>External conflict:</strong> The political and military struggle between Antony and Octavius Caesar for control of Rome.</li>\n  <li><strong>Rome versus Egypt:</strong> The symbolic conflict between discipline/empire and passion/pleasure that runs through the entire play.</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Moral Lessons for Modern Society</h3>\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  The play warns against letting personal passion override duty and sound judgement, especially for those in positions of leadership. It also shows the destructive power of jealousy and hasty decisions (Antony's rage against Cleopatra), the cost of political ambition (Caesar's ruthless pursuit of sole power), and the human capacity for dignity even in defeat (Cleopatra's chosen death over humiliation).\n</div>\n",
    "questions": [
      {
        "q": "What does this Second Term review topic consolidate?",
        "o": [
          "Only the unseen prose techniques",
          "Characterisation, plot development, major conflicts, and moral lessons of Antony and Cleopatra",
          "A completely new play not studied before",
          "Only the poems studied in First Term"
        ],
        "a": 1,
        "e": "This topic consolidates the whole of Antony and Cleopatra, drawing together characterisation, plot, conflicts, and moral lessons.",
        "h": "Consider the purpose of a review topic in the second term.",
        "yr": "SS3"
      },
      {
        "q": "How is Antony's character summarised in the review?",
        "o": [
          "As a once-great soldier undone by passion, torn between duty and love",
          "As a villain with no redeeming qualities",
          "As a coward who never loved Cleopatra",
          "As entirely loyal to Rome with no conflict"
        ],
        "a": 0,
        "e": "Antony is summarised as a once-great soldier undone by passion, torn between duty and love, but ultimately noble in his final loyalty to Cleopatra.",
        "h": "Consider the balance of strength and weakness in his character.",
        "yr": "SS3"
      },
      {
        "q": "How is Cleopatra's character summarised in the review?",
        "o": [
          "Entirely selfless with no flaws",
          "Completely indifferent to Antony",
          "Complex and theatrical, capable of genuine love but also manipulation",
          "A minor character with little significance"
        ],
        "a": 2,
        "e": "Cleopatra is summarised as complex, theatrical, and politically astute, capable of genuine love but also manipulation, dying with dignity.",
        "h": "Consider her multifaceted, contradictory nature.",
        "yr": "SS3"
      },
      {
        "q": "How is Octavius Caesar's character summarised?",
        "o": [
          "Passionate and reckless like Antony",
          "Calculating, ambitious, and self-controlled, representing cold political reason",
          "Loyal only to Cleopatra",
          "A weak and indecisive ruler"
        ],
        "a": 1,
        "e": "Caesar is summarised as calculating, ambitious, and self-controlled, representing cold political reason over passion.",
        "h": "Consider his role as a foil to Antony's passionate nature.",
        "yr": "SS3"
      },
      {
        "q": "What does Enobarbus's desertion and death show in the review?",
        "o": [
          "The tragic cost of choosing self-preservation over loyalty",
          "That Enobarbus was never truly loyal to Antony",
          "That loyalty is never tested in the play",
          "That Caesar rewarded Enobarbus generously"
        ],
        "a": 0,
        "e": "Enobarbus's desertion and death show the tragic cost of choosing self-preservation over loyalty.",
        "h": "Consider the emotional consequence of his choice to desert.",
        "yr": "SS3"
      },
      {
        "q": "What happens in Act I that begins the play's plot arc?",
        "o": [
          "The Battle of Actium",
          "Caesar's marriage to Cleopatra",
          "Antony's death",
          "Antony's initial neglect of Rome for Egypt"
        ],
        "a": 3,
        "e": "The plot moves from Antony's initial neglect of Rome for Egypt in Act I, beginning the central conflict.",
        "h": "Consider the opening event that sets the tragedy in motion.",
        "yr": "SS3"
      },
      {
        "q": "What temporary resolution occurs in Act II of the plot arc?",
        "o": [
          "Cleopatra's suicide",
          "Reconciliation with Caesar via marriage to Octavia",
          "Antony's death",
          "The final defeat of Rome"
        ],
        "a": 1,
        "e": "Act II brings a temporary reconciliation between Antony and Caesar, sealed by Antony's marriage to Octavia.",
        "h": "Consider the political marriage arranged in this act.",
        "yr": "SS3"
      },
      {
        "q": "What catastrophic choice occurs in Act III of the plot arc?",
        "o": [
          "Antony marries Cleopatra",
          "Caesar surrenders to Antony",
          "Antony follows Cleopatra at the Battle of Actium",
          "Cleopatra dies"
        ],
        "a": 2,
        "e": "Act III brings the catastrophic choice of Antony following Cleopatra's fleeing ship at the Battle of Actium.",
        "h": "Consider the naval battle that changes the course of the play.",
        "yr": "SS3"
      },
      {
        "q": "What happens in Acts IV and V of the plot arc?",
        "o": [
          "Antony's return to power in Rome",
          "The deaths of Enobarbus, Antony, and Cleopatra",
          "A lasting peace between Rome and Egypt",
          "The rise of Pompey to power"
        ],
        "a": 1,
        "e": "Acts IV and V bring the deaths of Enobarbus, Antony, and Cleopatra, resolving the tragedy.",
        "h": "Consider the tragic conclusion of the play's central characters.",
        "yr": "SS3"
      },
      {
        "q": "What does each act of the play deepen, according to the review?",
        "o": [
          "The central conflict between reason/duty (Rome) and passion/pleasure (Egypt)",
          "The relationship between Caesar and Octavia",
          "A comic subplot unrelated to the main story",
          "A minor historical detail with no thematic weight"
        ],
        "a": 0,
        "e": "Each act deepens the central conflict between reason/duty (Rome) and passion/pleasure (Egypt).",
        "h": "This is the play's core symbolic and thematic structure.",
        "yr": "SS3"
      },
      {
        "q": "What is Antony's internal conflict, as summarised in the review?",
        "o": [
          "His hatred for Cleopatra",
          "His struggle between his Roman identity and his love for Cleopatra",
          "His desire to become Emperor of Rome",
          "His indifference to both Rome and Egypt"
        ],
        "a": 1,
        "e": "Antony's internal conflict is his struggle between his Roman identity and his love for Cleopatra.",
        "h": "Consider the personal, psychological dimension of his tragedy.",
        "yr": "SS3"
      },
      {
        "q": "What is the external conflict in the play, as summarised in the review?",
        "o": [
          "The political and military struggle between Antony and Caesar for control of Rome",
          "A conflict between Cleopatra and Octavia only",
          "A conflict between Enobarbus and Eros",
          "A war between Egypt and Parthia"
        ],
        "a": 0,
        "e": "The external conflict is the political and military struggle between Antony and Octavius Caesar for control of Rome.",
        "h": "This concerns the larger power struggle beyond personal relationships.",
        "yr": "SS3"
      },
      {
        "q": "What does the symbolic 'Rome versus Egypt' conflict represent?",
        "o": [
          "A conflict resolved early in Act I",
          "A literal war fought only in the final act",
          "Discipline/empire versus passion/pleasure running through the entire play",
          "A minor subplot involving Pompey"
        ],
        "a": 2,
        "e": "The Rome-versus-Egypt conflict symbolically represents discipline/empire versus passion/pleasure, running through the entire play.",
        "h": "This is the overarching symbolic framework of the tragedy.",
        "yr": "SS3"
      },
      {
        "q": "What moral lesson does the play offer about leadership and passion?",
        "o": [
          "It warns against letting personal passion override duty and sound judgement",
          "It suggests passion always leads to success",
          "It offers no lessons relevant to leadership",
          "It encourages leaders to ignore all personal relationships"
        ],
        "a": 0,
        "e": "The play warns against letting personal passion override duty and sound judgement, especially for those in leadership positions.",
        "h": "Consider Antony's downfall as a cautionary example.",
        "yr": "SS3"
      },
      {
        "q": "What moral lesson does the play offer through Antony's rage against Cleopatra?",
        "o": [
          "The value of open communication",
          "The benefits of careful, calm decision-making",
          "The destructive power of jealousy and hasty decisions",
          "The importance of trusting rumours immediately"
        ],
        "a": 2,
        "e": "The play shows the destructive power of jealousy and hasty decisions through Antony's furious, mistaken rage against Cleopatra.",
        "h": "Consider the consequences of Antony's whipping of Thidias and his accusations against Cleopatra.",
        "yr": "SS3"
      },
      {
        "q": "What moral lesson does the play offer through Caesar's pursuit of power?",
        "o": [
          "The importance of loyalty to friends above all else",
          "The dangers of romantic passion alone",
          "The cost of political ambition, shown through his ruthless pursuit of sole power",
          "The benefits of sharing power equally with others"
        ],
        "a": 2,
        "e": "The play shows the cost of political ambition through Caesar's ruthless pursuit of sole power over Rome.",
        "h": "Consider Caesar's cold, calculating rise to become sole ruler.",
        "yr": "SS3"
      },
      {
        "q": "What moral lesson does Cleopatra's death offer to a modern audience?",
        "o": [
          "The value of avoiding all risk",
          "The importance of surrendering unconditionally",
          "Human capacity for dignity even in defeat",
          "The benefits of trusting one's enemies completely"
        ],
        "a": 2,
        "e": "Cleopatra's chosen death over humiliation shows the human capacity for dignity even in defeat.",
        "h": "Consider why Cleopatra chooses suicide rather than being paraded by Caesar.",
        "yr": "SS3"
      },
      {
        "q": "Why is this review topic important before facing exam questions on the play?",
        "o": [
          "It consolidates knowledge across all five acts into key themes and characters",
          "It focuses only on minor details irrelevant to exams",
          "It introduces entirely new characters not previously studied",
          "It replaces the need to study the individual acts"
        ],
        "a": 0,
        "e": "This review topic is important because it consolidates knowledge across all five acts into key themes and characters, useful for exam preparation.",
        "h": "Consider the value of synthesis before an examination.",
        "yr": "SS3"
      },
      {
        "q": "Which theme connects both Antony's downfall and Caesar's rise to power?",
        "o": [
          "A shared love of Egypt",
          "A shared belief in democracy",
          "The tension between passion and cold political reason",
          "A mutual hatred of Cleopatra"
        ],
        "a": 2,
        "e": "The tension between passion (Antony) and cold political reason (Caesar) connects both characters' fates in the play.",
        "h": "Consider how these two contrasting approaches to power drive the plot.",
        "yr": "SS3"
      },
      {
        "q": "What overall impression of Antony does the review leave the reader with?",
        "o": [
          "A minor, forgettable character in the play",
          "A tragic figure, great but ultimately undone by his own passion",
          "An entirely successful and triumphant ruler",
          "A purely villainous character with no positive qualities"
        ],
        "a": 1,
        "e": "The review leaves the impression of Antony as a tragic figure — great but ultimately undone by his own passion.",
        "h": "Consider the balance of greatness and flaw that defines a tragic hero.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "African Prose — So the Path Does Not Die & Redemption Road",
    "topicCode": "SS3-LIT-10",
    "module": "African Prose",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic covers the two prescribed African prose texts: Pede Hollist's <strong>So the Path Does Not Die</strong> and Elma Shaw's <strong>Redemption Road</strong>, both of which confront painful chapters of West African social history through the eyes of resilient women.\n</div>\n\n<h3 class=\"learn-subheading\">1. So the Path Does Not Die — Pede Hollist</h3>\n<p class=\"learn-p\">Set largely in Sierra Leone and the United States, the novel follows <strong>Finaba (\"Fina\") Marah</strong>, a girl whose traditional female initiation (circumcision) ceremony is interrupted before it can be completed. Fina emigrates to America, where she builds a new, \"fashionable\" life in Washington, D.C., but is haunted by her broken initiation and her sense of not fully belonging in either world. When Sierra Leone's civil war breaks out, she is drawn back home to search for her grandmother; unable to find her, Fina instead stays to help traumatised women and girls who survived the war, symbolically completing — and redefining — the \"path\" that was interrupted in her own youth.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist</td><td>Finaba \"Fina\" Marah</td></tr>\n      <tr><td>Setting</td><td>Rural Sierra Leone, Freetown, and Washington, D.C., USA</td></tr>\n      <tr><td>Key themes</td><td>Female circumcision/initiation, tradition vs. change, diaspora and \"otherness,\" war, healing and restoration</td></tr>\n      <tr><td>Narrative structure</td><td>A bildungsroman (coming-of-age story) that begins and ends in Africa</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Redemption Road — Elma Shaw</h3>\n<p class=\"learn-p\">Set in Monrovia, Liberia, during and after Charles Taylor's rule, the novel follows <strong>Bendu Lewis</strong>, a young woman who counsels other survivors of Liberia's civil war while privately struggling with trauma from her own wartime experiences. When the warlord who once held her captive — known as <strong>Commander Cobra</strong> — resurfaces in her town, Bendu resolves to pursue justice for his past atrocities, a quest that forces her to finally confront and make peace with her own painful, long-hidden wartime secret.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist</td><td>Bendu Lewis</td></tr>\n      <tr><td>Antagonist</td><td>Commander Cobra, a former warlord</td></tr>\n      <tr><td>Setting</td><td>Post-war Monrovia, Liberia</td></tr>\n      <tr><td>Key themes</td><td>Trauma and healing, justice and accountability, atonement, the legacy of civil war, resilience of women</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Comparing the Two Novels</h3>\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  Both novels centre on strong West African women confronting deeply painful personal and cultural wounds — Fina's interrupted initiation and Bendu's wartime trauma — and both end with their protagonists choosing to help other women and girls, transforming personal pain into a source of healing for their communities.\n</div>\n",
    "questions": [
      {
        "q": "Who is the protagonist of So the Path Does Not Die?",
        "o": [
          "Anansewa",
          "Finaba 'Fina' Marah",
          "Bendu Lewis",
          "Commander Cobra"
        ],
        "a": 1,
        "e": "The protagonist of So the Path Does Not Die is Finaba 'Fina' Marah.",
        "h": "This character is also referred to by a shortened version of her name.",
        "yr": "SS3"
      },
      {
        "q": "What traditional practice does Fina's story centre on?",
        "o": [
          "Female circumcision/initiation",
          "Ancestor worship",
          "Coming-of-age hunting rites",
          "Arranged marriage"
        ],
        "a": 0,
        "e": "Fina's story centres on her interrupted traditional female circumcision/initiation ceremony.",
        "h": "This ceremony is broken off before completion in the novel.",
        "yr": "SS3"
      },
      {
        "q": "Where is So the Path Does Not Die primarily set?",
        "o": [
          "Nigeria and London",
          "Liberia and New York",
          "Rural Sierra Leone, Freetown, and Washington, D.C.",
          "Ghana and Paris"
        ],
        "a": 2,
        "e": "The novel is primarily set in rural Sierra Leone, Freetown, and Washington, D.C., USA.",
        "h": "Consider the two countries connected by Fina's journey.",
        "yr": "SS3"
      },
      {
        "q": "Why does Fina emigrate to America?",
        "o": [
          "To study medicine at a specific university",
          "To join the Sierra Leonean government in exile",
          "To marry an American diplomat",
          "To escape the aftermath of her interrupted initiation and build a new life"
        ],
        "a": 3,
        "e": "Fina emigrates to America partly to escape the aftermath of her interrupted initiation and build a new, fashionable life.",
        "h": "Consider her personal motivation tied to her earlier trauma.",
        "yr": "SS3"
      },
      {
        "q": "What draws Fina back to Sierra Leone?",
        "o": [
          "A political campaign",
          "An invitation to a wedding",
          "The outbreak of Sierra Leone's civil war and her search for her grandmother",
          "A new job offer in Freetown"
        ],
        "a": 2,
        "e": "Fina is drawn back to Sierra Leone by the outbreak of the civil war and her search for her grandmother.",
        "h": "Consider the national crisis that prompts her return.",
        "yr": "SS3"
      },
      {
        "q": "What does Fina do when she cannot find her grandmother?",
        "o": [
          "She abandons her search entirely and does nothing further",
          "She stays to help traumatised women and girls who survived the war",
          "She joins the rebel forces",
          "She immediately returns to America"
        ],
        "a": 1,
        "e": "Unable to find her grandmother, Fina stays to help traumatised women and girls who survived the war.",
        "h": "Consider how her personal quest transforms into a broader mission.",
        "yr": "SS3"
      },
      {
        "q": "What does Fina's decision to help war survivors symbolically represent?",
        "o": [
          "A refusal to ever return to America",
          "A complete rejection of her Sierra Leonean heritage",
          "Completing and redefining the 'path' interrupted in her own youth",
          "Her total abandonment of Western values"
        ],
        "a": 2,
        "e": "Fina's decision symbolically represents completing and redefining the 'path' that was interrupted in her own youth.",
        "h": "This connects the novel's title to its resolution.",
        "yr": "SS3"
      },
      {
        "q": "What literary form does So the Path Does Not Die take?",
        "o": [
          "A collection of short stories",
          "A stage play",
          "A bildungsroman (coming-of-age story)",
          "An epic poem"
        ],
        "a": 2,
        "e": "So the Path Does Not Die is structured as a bildungsroman, a coming-of-age story that begins and ends in Africa.",
        "h": "This literary term describes a novel following a character's growth and development.",
        "yr": "SS3"
      },
      {
        "q": "Who is the protagonist of Redemption Road?",
        "o": [
          "Desola",
          "Bendu Lewis",
          "Finaba Marah",
          "Anansewa"
        ],
        "a": 1,
        "e": "The protagonist of Redemption Road is Bendu Lewis.",
        "h": "This character works as a counsellor for war survivors.",
        "yr": "SS3"
      },
      {
        "q": "What is Bendu Lewis's profession in Redemption Road?",
        "o": [
          "She is a professional soldier",
          "She counsels traumatised survivors of Liberia's civil war",
          "She is a schoolteacher for young children",
          "She is a government minister"
        ],
        "a": 1,
        "e": "Bendu Lewis counsels traumatised survivors of Liberia's civil war in the novel.",
        "h": "Consider her role in helping others process wartime trauma.",
        "yr": "SS3"
      },
      {
        "q": "What personal struggle does Bendu privately face?",
        "o": [
          "Memories of her own wartime experiences",
          "A struggle to find employment",
          "A conflict with her own daughter",
          "A dispute over inherited property"
        ],
        "a": 0,
        "e": "Bendu privately struggles with memories of her own traumatic wartime experiences while helping others.",
        "h": "Consider the irony of her counselling others while hiding her own pain.",
        "yr": "SS3"
      },
      {
        "q": "Who is Commander Cobra in Redemption Road?",
        "o": [
          "The warlord who once held Bendu captive",
          "Bendu's brother",
          "Bendu's husband",
          "A Liberian government official"
        ],
        "a": 0,
        "e": "Commander Cobra is the warlord who once held Bendu captive during the civil war.",
        "h": "This character resurfaces in Bendu's town, prompting the novel's central conflict.",
        "yr": "SS3"
      },
      {
        "q": "What motivates Bendu to pursue justice against Commander Cobra?",
        "o": [
          "Her own healing and the voiceless victims of the war",
          "Pressure from the Liberian government",
          "A desire for financial compensation",
          "A promise made to her mother"
        ],
        "a": 0,
        "e": "Bendu is motivated by her own healing and a desire for justice on behalf of the voiceless victims of the war.",
        "h": "Consider both personal and communal motivations driving her quest.",
        "yr": "SS3"
      },
      {
        "q": "What does Bendu ultimately confront in her pursuit of Commander Cobra?",
        "o": [
          "A financial scandal",
          "Her own painful, long-hidden wartime secret",
          "A rival counsellor at her NGO",
          "Her fear of public speaking"
        ],
        "a": 1,
        "e": "In pursuing Commander Cobra, Bendu ultimately confronts and makes peace with her own painful, long-hidden wartime secret.",
        "h": "Consider how the external quest for justice becomes an internal journey.",
        "yr": "SS3"
      },
      {
        "q": "Where is Redemption Road primarily set?",
        "o": [
          "Freetown, Sierra Leone",
          "Accra, Ghana",
          "Monrovia, Liberia",
          "Lagos, Nigeria"
        ],
        "a": 2,
        "e": "Redemption Road is set in Monrovia, Liberia, during and after Charles Taylor's administration.",
        "h": "Consider the capital city of Liberia.",
        "yr": "SS3"
      },
      {
        "q": "Which Liberian leader's administration provides the novel's historical backdrop?",
        "o": [
          "Charles Taylor",
          "William Tubman",
          "Ellen Johnson Sirleaf",
          "Samuel Doe"
        ],
        "a": 0,
        "e": "The novel is set during and after the administration of Liberia's former president, Charles Taylor.",
        "h": "This leader's rule is tied to the Liberian civil war era.",
        "yr": "SS3"
      },
      {
        "q": "What key theme do both So the Path Does Not Die and Redemption Road share?",
        "o": [
          "A focus on wealthy urban elites",
          "A shared setting in the same country",
          "West African women confronting deeply painful personal and cultural wounds",
          "A rejection of all traditional customs"
        ],
        "a": 2,
        "e": "Both novels centre on strong West African women confronting deeply painful personal and cultural wounds.",
        "h": "Consider the parallel struggles of Fina and Bendu.",
        "yr": "SS3"
      },
      {
        "q": "How do both novels' protagonists resolve their personal struggles?",
        "o": [
          "By choosing to help other women and girls, transforming pain into healing",
          "By abandoning their home countries permanently",
          "By ignoring their trauma completely",
          "By seeking revenge without any resolution"
        ],
        "a": 0,
        "e": "Both protagonists choose to help other women and girls, transforming their personal pain into a source of healing for their communities.",
        "h": "Consider the parallel endings of both novels.",
        "yr": "SS3"
      },
      {
        "q": "What broader historical events form the backdrop of both novels?",
        "o": [
          "The transatlantic slave trade",
          "The independence movements of the 1960s",
          "West African civil wars (Sierra Leone and Liberia)",
          "European colonisation of West Africa"
        ],
        "a": 2,
        "e": "Both novels are set against the backdrop of West African civil wars — Sierra Leone's in Fina's story and Liberia's in Bendu's.",
        "h": "Consider the conflicts that shape both protagonists' journeys.",
        "yr": "SS3"
      },
      {
        "q": "What genre do both So the Path Does Not Die and Redemption Road belong to?",
        "o": [
          "Stage drama",
          "Poetry collections",
          "African prose (novels)",
          "Epic oral narrative"
        ],
        "a": 2,
        "e": "Both texts are African prose works — novels — prescribed for the Literature-in-English syllabus.",
        "h": "Consider their classification within the WAEC harmonised text list.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Non-African Prose — To Kill a Mockingbird & Path of Lucas",
    "topicCode": "SS3-LIT-11",
    "module": "Non-African Prose",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic covers Harper Lee's classic American novel <strong>To Kill a Mockingbird</strong> and Susanne Bellefeuille's biographical novel <strong>Path of Lucas: The Journey He Endured</strong>.\n</div>\n\n<h3 class=\"learn-subheading\">1. To Kill a Mockingbird — Harper Lee</h3>\n<p class=\"learn-p\">Set in the fictional town of <strong>Maycomb, Alabama</strong>, during the 1930s, the novel is narrated by <strong>Jean Louise \"Scout\" Finch</strong>, who recalls her childhood with her older brother Jem and their friend Dill. Their father, <strong>Atticus Finch</strong>, a principled lawyer, defends <strong>Tom Robinson</strong>, a Black man falsely accused of raping a white woman, Mayella Ewell. Despite clear evidence of Tom's innocence, an all-white jury convicts him, reflecting the deep racial injustice of the American South. A parallel plot follows the children's fascination with their reclusive neighbour, <strong>Boo Radley</strong>, who ultimately saves Jem and Scout from an attack by Mayella's vengeful father, Bob Ewell.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Narrator/Protagonist</td><td>Scout Finch</td></tr>\n      <tr><td>Key characters</td><td>Atticus Finch, Jem Finch, Tom Robinson, Boo Radley, Bob and Mayella Ewell</td></tr>\n      <tr><td>Setting</td><td>Maycomb, Alabama, USA, during the Great Depression</td></tr>\n      <tr><td>Key themes</td><td>Racial injustice, loss of innocence, moral courage, empathy (\"walking in someone else's skin\"), prejudice</td></tr>\n      <tr><td>Title symbolism</td><td>A \"mockingbird\" symbolises innocent beings who are harmed for doing no wrong — such as Tom Robinson and Boo Radley.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Path of Lucas: The Journey He Endured — Susanne Bellefeuille</h3>\n<p class=\"learn-p\">Based on true events from the author's own family, the novel follows <strong>Lucas Clarkson</strong>, a hardworking farmer's son from a small town in Eastern Ontario, Canada, who becomes a successful mechanic. Facing a family crisis after his daughter <strong>Lucy</strong> is left comatose by a car crash, Lucas recounts his life's story — including his courtship of his wife <strong>Isabelle</strong> — in a desperate attempt to reach and anchor his unconscious daughter back to the world of the living.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist</td><td>Lucas Clarkson</td></tr>\n      <tr><td>Key characters</td><td>Isabelle (Lucas's wife), Lucy (their comatose daughter)</td></tr>\n      <tr><td>Setting</td><td>A small farming town in Eastern Ontario, Canada</td></tr>\n      <tr><td>Key themes</td><td>Family devotion, perseverance through hardship, love and sacrifice, storytelling as a means of healing</td></tr>\n      <tr><td>Basis</td><td>Based on true events in the author's own family; Lucas is modelled on her father and Isabelle on her mother</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Both novels use a personal, close narrative voice — Scout's child narrator in Mockingbird and Lucas's own retrospective storytelling — to explore how ordinary families endure extraordinary hardship.\n</div>\n",
    "questions": [
      {
        "q": "Where is To Kill a Mockingbird set?",
        "o": [
          "New York City",
          "Maycomb, Alabama",
          "Chicago, Illinois",
          "London, England"
        ],
        "a": 1,
        "e": "The novel is set in the fictional town of Maycomb, Alabama, in the 1930s.",
        "h": "Think of a small town in the American South.",
        "yr": "SS3"
      },
      {
        "q": "Who narrates To Kill a Mockingbird?",
        "o": [
          "Atticus Finch",
          "Dill Harris",
          "Jean Louise 'Scout' Finch",
          "Jem Finch"
        ],
        "a": 2,
        "e": "The story is narrated by Jean Louise 'Scout' Finch, recalling her childhood.",
        "h": "The narrator is a young girl looking back on her childhood.",
        "yr": "SS3"
      },
      {
        "q": "What is Atticus Finch's profession?",
        "o": [
          "Farmer",
          "Doctor",
          "Lawyer",
          "Teacher"
        ],
        "a": 2,
        "e": "Atticus Finch is a principled lawyer in Maycomb.",
        "h": "He defends Tom Robinson in court.",
        "yr": "SS3"
      },
      {
        "q": "Who does Atticus defend in court?",
        "o": [
          "Bob Ewell",
          "Jem Finch",
          "Tom Robinson",
          "Boo Radley"
        ],
        "a": 2,
        "e": "Atticus defends Tom Robinson, a Black man falsely accused of raping a white woman.",
        "h": "This man is wrongly accused of a crime.",
        "yr": "SS3"
      },
      {
        "q": "Who accuses Tom Robinson of rape?",
        "o": [
          "Mayella Ewell",
          "Calpurnia",
          "Aunt Alexandra",
          "Miss Maudie"
        ],
        "a": 0,
        "e": "Mayella Ewell, a young white woman, accuses Tom Robinson, though evidence shows he is innocent.",
        "h": "She is the daughter of Bob Ewell.",
        "yr": "SS3"
      },
      {
        "q": "What is the outcome of Tom Robinson's trial?",
        "o": [
          "An all-white jury convicts him despite clear evidence of innocence",
          "He confesses",
          "The case is dismissed",
          "He is acquitted"
        ],
        "a": 0,
        "e": "Despite clear evidence of his innocence, an all-white jury convicts Tom, reflecting racial injustice.",
        "h": "Consider the racial prejudice of the jury.",
        "yr": "SS3"
      },
      {
        "q": "Who is Boo Radley?",
        "o": [
          "The town sheriff",
          "Tom Robinson's brother",
          "Scout's teacher",
          "A reclusive neighbour of the Finches"
        ],
        "a": 3,
        "e": "Boo Radley is the reclusive neighbour who fascinates the children and later saves them.",
        "h": "The children are curious and afraid of him at first.",
        "yr": "SS3"
      },
      {
        "q": "What does Boo Radley do at the end of the novel?",
        "o": [
          "He leaves Maycomb forever",
          "He saves Jem and Scout from Bob Ewell's attack",
          "He attacks Atticus",
          "He testifies at Tom's trial"
        ],
        "a": 1,
        "e": "Boo Radley saves Jem and Scout from an attack by Bob Ewell.",
        "h": "This act reveals his kindness.",
        "yr": "SS3"
      },
      {
        "q": "Who is Bob Ewell?",
        "o": [
          "Mayella's vengeful father",
          "Atticus's brother",
          "Jem's teacher",
          "The town judge"
        ],
        "a": 0,
        "e": "Bob Ewell is Mayella's father, who seeks revenge on Atticus and attacks the children.",
        "h": "He is the antagonist who lies at the trial.",
        "yr": "SS3"
      },
      {
        "q": "Which decade provides the novel's historical setting?",
        "o": [
          "The 1930s (Great Depression era)",
          "The 1990s",
          "The 1890s",
          "The 1960s"
        ],
        "a": 0,
        "e": "The novel is set in the 1930s, during the Great Depression.",
        "h": "Consider the era of economic hardship in America.",
        "yr": "SS3"
      },
      {
        "q": "What does the mockingbird symbolise?",
        "o": [
          "Innocent beings harmed for doing no wrong",
          "Wealth and status",
          "Political power",
          "Freedom of the press"
        ],
        "a": 0,
        "e": "The mockingbird symbolises innocent beings, like Tom Robinson and Boo Radley, who are harmed despite doing no wrong.",
        "h": "Atticus says it is a sin to kill one.",
        "yr": "SS3"
      },
      {
        "q": "Which character is a symbolic 'mockingbird' wrongly destroyed by prejudice?",
        "o": [
          "Judge Taylor",
          "Aunt Alexandra",
          "Tom Robinson",
          "Bob Ewell"
        ],
        "a": 2,
        "e": "Tom Robinson is a symbolic mockingbird — innocent, yet destroyed by racial prejudice.",
        "h": "He is the falsely accused man.",
        "yr": "SS3"
      },
      {
        "q": "Which is a central theme of To Kill a Mockingbird?",
        "o": [
          "Romantic love",
          "Political revolution",
          "Racial injustice",
          "Interplanetary travel"
        ],
        "a": 2,
        "e": "Racial injustice is a central theme of the novel.",
        "h": "It runs through Tom's trial.",
        "yr": "SS3"
      },
      {
        "q": "What lesson about empathy does Atticus teach Scout?",
        "o": [
          "To ignore the opinions of others",
          "To trust everyone",
          "To avoid all conflict",
          "To understand others by 'walking in their skin'"
        ],
        "a": 3,
        "e": "Atticus teaches Scout to understand people by considering things from their point of view — walking in their skin.",
        "h": "It concerns seeing through another's eyes.",
        "yr": "SS3"
      },
      {
        "q": "Which theme concerns the children's change from childhood to awareness of the adult world?",
        "o": [
          "Scientific discovery",
          "Political reform",
          "Loss of innocence",
          "Economic growth"
        ],
        "a": 2,
        "e": "Loss of innocence is a key theme as Scout and Jem confront prejudice and injustice.",
        "h": "Consider how the children grow up.",
        "yr": "SS3"
      },
      {
        "q": "Who is Lucas Clarkson in Path of Lucas?",
        "o": [
          "A soldier",
          "A famous lawyer",
          "A hardworking farmer's son who becomes a successful mechanic",
          "A schoolteacher"
        ],
        "a": 2,
        "e": "Lucas Clarkson is a farmer's son who becomes a successful mechanic in Eastern Ontario.",
        "h": "His work involves machines.",
        "yr": "SS3"
      },
      {
        "q": "Where is Path of Lucas set?",
        "o": [
          "Lagos, Nigeria",
          "London, England",
          "A small farming town in Eastern Ontario, Canada",
          "Alabama, USA"
        ],
        "a": 2,
        "e": "The novel is set in a small farming town in Eastern Ontario, Canada.",
        "h": "Think of a Canadian province.",
        "yr": "SS3"
      },
      {
        "q": "Why does Lucas tell his life story in the novel?",
        "o": [
          "To entertain his grandchildren",
          "To reach and anchor his comatose daughter Lucy to the living world",
          "To testify in court",
          "To write his autobiography for publication"
        ],
        "a": 1,
        "e": "Lucas recounts his life to his comatose daughter Lucy in a desperate bid to anchor her to the living world.",
        "h": "It follows a car crash.",
        "yr": "SS3"
      },
      {
        "q": "Who is Isabelle in Path of Lucas?",
        "o": [
          "Lucas's wife",
          "Lucas's sister",
          "Lucy's teacher",
          "Lucas's employer"
        ],
        "a": 0,
        "e": "Isabelle is Lucas's wife, whose courtship he recounts.",
        "h": "She is Lucy's mother.",
        "yr": "SS3"
      },
      {
        "q": "On whose real family is Path of Lucas based?",
        "o": [
          "The author's teacher",
          "A historical soldier",
          "A famous Canadian politician",
          "The author's own parents"
        ],
        "a": 3,
        "e": "The novel is based on true events from the author's own family — Lucas on her father and Isabelle on her mother.",
        "h": "It is a biographical novel.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "African Drama — The Marriage of Anansewa & Once Upon an Elephant",
    "topicCode": "SS3-LIT-12",
    "module": "African Drama",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic covers Efua Sutherland's Ghanaian classic <strong>The Marriage of Anansewa</strong> and Bosede Ademilua-Afolayan's Nigerian play <strong>Once Upon an Elephant</strong>, both rooted in traditional African storytelling techniques.\n</div>\n\n<h3 class=\"learn-subheading\">1. The Marriage of Anansewa — Efua Sutherland</h3>\n<p class=\"learn-p\">Written in the tradition of Akan \"Anansesem\" (spider/Ananse storytelling), the play follows <strong>Ananse</strong>, a poor but cunning man, who tries to secure his fortune by promising his daughter <strong>Anansewa</strong> in marriage to four wealthy chiefs at once, hoping to collect dowry gifts from each without any of them discovering the deception. His scheme is complicated when all four suitors arrive at the same time, forcing Ananse into increasingly desperate lies — including faking Anansewa's death — to avoid exposure. The play uses a <strong>storyteller (the \"Player\")</strong> who directly narrates and comments on the action, breaking the fourth wall in the tradition of oral storytelling.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist</td><td>Ananse (Kwaku Ananse)</td></tr>\n      <tr><td>Key character</td><td>Anansewa, his daughter</td></tr>\n      <tr><td>Genre/style</td><td>A \"storytelling drama\" — combines Western dramatic form with Akan oral storytelling conventions</td></tr>\n      <tr><td>Key themes</td><td>Greed and cunning, the commodification of marriage, deception, satire of materialism in modern society</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Once Upon an Elephant — Bosede Ademilua-Afolayan</h3>\n<p class=\"learn-p\">Using Yoruba oral traditions and folktale techniques, the play tells of <strong>Ajanaku (Olaniyonu)</strong>, who is not the rightful heir to the throne but ascends it with the help of the kingmaker and chief priest, <strong>Serubawon</strong>. Ajanaku's reign becomes tyrannical, marked by terror and abuse of power; his crimes culminate in the rape of the virgin <strong>Desola</strong> as part of a ritual he believes will grant him immortality. This atrocity sets in motion his eventual downfall, as his insatiable lust for power, wealth, and fame ultimately leads to his death.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist/Antagonist</td><td>Ajanaku (Olaniyonu), the tyrant king</td></tr>\n      <tr><td>Key characters</td><td>Serubawon (kingmaker/chief priest), Desola (his victim), Akinjobi (the previous king)</td></tr>\n      <tr><td>Style</td><td>Uses Yoruba folklore and oral storytelling conventions to critique dictatorship</td></tr>\n      <tr><td>Key themes</td><td>Tyranny and abuse of power, \"sit-tightism\" (leaders refusing to relinquish power), corruption, justice and downfall of dictators</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Comparing the Plays:</strong> Both playwrights draw on indigenous African oral storytelling forms — Sutherland uses Akan Anansesem and a narrating \"Player,\" while Ademilua-Afolayan uses Yoruba folktale conventions — to critique universal human failings: greed in Anansewa, and tyranny in Once Upon an Elephant.\n</div>\n",
    "questions": [
      {
        "q": "Who wrote The Marriage of Anansewa?",
        "o": [
          "Wole Soyinka",
          "Efua Sutherland",
          "Chinua Achebe",
          "Bosede Ademilua-Afolayan"
        ],
        "a": 1,
        "e": "The Marriage of Anansewa was written by the Ghanaian playwright Efua Sutherland.",
        "h": "She is a Ghanaian writer.",
        "yr": "SS3"
      },
      {
        "q": "What storytelling tradition does The Marriage of Anansewa draw on?",
        "o": [
          "Akan Anansesem (spider stories)",
          "Greek tragedy",
          "Yoruba masquerade",
          "Roman comedy"
        ],
        "a": 0,
        "e": "The play draws on the Akan tradition of Anansesem, the Ananse spider stories.",
        "h": "Ananse is a trickster spider figure.",
        "yr": "SS3"
      },
      {
        "q": "Who is Ananse in the play?",
        "o": [
          "A village priest",
          "A poor but cunning man and father of Anansewa",
          "A Roman general",
          "A powerful chief"
        ],
        "a": 1,
        "e": "Ananse is a poor but cunning man who tries to secure his fortune through his daughter's marriage.",
        "h": "He schemes to grow rich.",
        "yr": "SS3"
      },
      {
        "q": "What is Ananse's scheme?",
        "o": [
          "To send Anansewa abroad to study",
          "To become a chief himself",
          "To sell his farm",
          "To promise Anansewa in marriage to four wealthy chiefs and collect gifts from each"
        ],
        "a": 3,
        "e": "Ananse promises Anansewa to four wealthy chiefs at once to collect dowry gifts from each.",
        "h": "It involves several suitors at the same time.",
        "yr": "SS3"
      },
      {
        "q": "What complicates Ananse's scheme?",
        "o": [
          "The chiefs discover his lies immediately",
          "Anansewa refuses to marry",
          "Ananse loses his money",
          "All four suitors arrive at the same time"
        ],
        "a": 3,
        "e": "The four suitors arrive simultaneously, forcing Ananse into increasingly desperate lies.",
        "h": "The timing of the visits is the problem.",
        "yr": "SS3"
      },
      {
        "q": "What drastic lie does Ananse use to avoid exposure?",
        "o": [
          "Burning his house",
          "Faking Anansewa's death",
          "Claiming he is a chief",
          "Leaving the village"
        ],
        "a": 1,
        "e": "Ananse fakes Anansewa's death to avoid exposure.",
        "h": "It is a false report of a tragedy.",
        "yr": "SS3"
      },
      {
        "q": "What is the role of the 'Player' in The Marriage of Anansewa?",
        "o": [
          "Anansewa's suitor",
          "A storyteller who narrates and comments on the action",
          "The villain of the play",
          "A silent extra"
        ],
        "a": 1,
        "e": "The Player is a storyteller who narrates and comments on the action, breaking the fourth wall.",
        "h": "It reflects oral storytelling tradition.",
        "yr": "SS3"
      },
      {
        "q": "Which is a key theme of The Marriage of Anansewa?",
        "o": [
          "Religious conversion",
          "Colonial conquest",
          "Civil war",
          "Greed and the commodification of marriage"
        ],
        "a": 3,
        "e": "Greed and the commodification of marriage, with satire of materialism, are key themes.",
        "h": "Marriage is treated like a business deal.",
        "yr": "SS3"
      },
      {
        "q": "Why is The Marriage of Anansewa called a 'storytelling drama'?",
        "o": [
          "It is based on a novel",
          "It is entirely sung",
          "It combines Western dramatic form with Akan oral storytelling conventions",
          "It has no dialogue"
        ],
        "a": 2,
        "e": "It combines Western dramatic form with Akan oral storytelling conventions.",
        "h": "Two traditions are blended.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote Once Upon an Elephant?",
        "o": [
          "Bosede Ademilua-Afolayan",
          "Femi Osofisan",
          "Wole Soyinka",
          "Efua Sutherland"
        ],
        "a": 0,
        "e": "Once Upon an Elephant was written by the Nigerian playwright Bosede Ademilua-Afolayan.",
        "h": "She is a Nigerian dramatist and academic.",
        "yr": "SS3"
      },
      {
        "q": "Which oral tradition does Once Upon an Elephant use?",
        "o": [
          "Greek myth",
          "Yoruba folktales and oral storytelling",
          "Igbo proverbs only",
          "Akan Anansesem"
        ],
        "a": 1,
        "e": "The play uses Yoruba oral traditions and folktale techniques.",
        "h": "It is rooted in south-western Nigerian culture.",
        "yr": "SS3"
      },
      {
        "q": "Who is the tyrant king in Once Upon an Elephant?",
        "o": [
          "Ajanaku (Olaniyonu)",
          "Akinjobi",
          "Serubawon",
          "Ananse"
        ],
        "a": 0,
        "e": "Ajanaku, also called Olaniyonu, is the tyrant king who seizes the throne.",
        "h": "He is not the rightful heir.",
        "yr": "SS3"
      },
      {
        "q": "Who helps Ajanaku ascend the throne?",
        "o": [
          "Akinjobi",
          "Serubawon, the kingmaker and chief priest",
          "Desola",
          "Anansewa"
        ],
        "a": 1,
        "e": "The kingmaker and chief priest Serubawon helps Ajanaku ascend the throne.",
        "h": "He is both a priest and a power broker.",
        "yr": "SS3"
      },
      {
        "q": "Who is Akinjobi?",
        "o": [
          "The previous king",
          "The tyrant's son",
          "The victim of the ritual",
          "A village priest"
        ],
        "a": 0,
        "e": "Akinjobi is the reigning king before Ajanaku takes the throne.",
        "h": "His reign is said to be ending.",
        "yr": "SS3"
      },
      {
        "q": "Who is Desola?",
        "o": [
          "The queen mother",
          "A palace guard",
          "The virgin whose rape by the king sets off his downfall",
          "Ajanaku's advisor"
        ],
        "a": 2,
        "e": "Desola is the virgin raped by the king as part of his ritual for immortality, which sets off his downfall.",
        "h": "She is his victim.",
        "yr": "SS3"
      },
      {
        "q": "Why does Ajanaku commit the atrocity against Desola?",
        "o": [
          "To please the chiefs",
          "To win a war",
          "As part of a ritual he believes will grant him immortality",
          "To punish a rival"
        ],
        "a": 2,
        "e": "He believes the ritual will grant him immortality.",
        "h": "His obsession is with living forever.",
        "yr": "SS3"
      },
      {
        "q": "What ultimately happens to Ajanaku?",
        "o": [
          "He abdicates peacefully",
          "His lust for power, wealth and fame leads to his death",
          "He becomes immortal",
          "He is exiled and lives comfortably"
        ],
        "a": 1,
        "e": "His insatiable lust for power, wealth and fame leads to his death.",
        "h": "The tyrant is brought down.",
        "yr": "SS3"
      },
      {
        "q": "Which theme does Once Upon an Elephant most strongly address?",
        "o": [
          "Romantic love",
          "Colonial education",
          "Economic trade",
          "Tyranny and dictatorship, including 'sit-tightism'"
        ],
        "a": 3,
        "e": "The play critiques tyranny, dictatorship and 'sit-tightism', leaders refusing to leave power.",
        "h": "It is a political play.",
        "yr": "SS3"
      },
      {
        "q": "What do both African plays share in technique?",
        "o": [
          "Use of no spoken dialogue",
          "Use of Shakespearean verse",
          "Use of indigenous African oral storytelling forms",
          "Use of Roman settings"
        ],
        "a": 2,
        "e": "Both draw on indigenous oral storytelling — Akan Anansesem and Yoruba folktale conventions.",
        "h": "Consider their cultural roots.",
        "yr": "SS3"
      },
      {
        "q": "What human failings do the two plays criticise respectively?",
        "o": [
          "Tyranny in Anansewa and greed in Once Upon an Elephant",
          "Cowardice in both",
          "Laziness in both",
          "Greed in Anansewa and tyranny in Once Upon an Elephant"
        ],
        "a": 3,
        "e": "Anansewa satirises greed; Once Upon an Elephant critiques tyranny.",
        "h": "Match each play to its main vice.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Non-African Drama — An Inspector Calls & A Man for All Seasons",
    "topicCode": "SS3-LIT-13",
    "module": "Non-African Drama",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic covers J. B. Priestley's <strong>An Inspector Calls</strong> and Robert Bolt's <strong>A Man for All Seasons</strong>, two plays that explore themes of social responsibility, integrity, and conscience.\n</div>\n\n<h3 class=\"learn-subheading\">1. An Inspector Calls — J. B. Priestley</h3>\n<p class=\"learn-p\">Set in 1912, the play centres on the wealthy <strong>Birling family</strong>, whose celebratory dinner is interrupted by the mysterious <strong>Inspector Goole</strong>, investigating the suicide of a young working-class woman, <strong>Eva Smith</strong> (also known as Daisy Renton). One by one, the Inspector reveals that every member of the family — Arthur Birling, Sybil Birling, their children Sheila and Eric, and Sheila's fiancé Gerald Croft — contributed, through selfishness or cruelty, to the chain of events that drove Eva to her death. The play ends ambiguously, questioning whether the Inspector was real, but reinforcing that the family's moral guilt remains regardless.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Key characters</td><td>Inspector Goole, Arthur & Sybil Birling, Sheila, Eric, Gerald Croft, Eva Smith/Daisy Renton</td></tr>\n      <tr><td>Setting</td><td>The Birling family's dining room, 1912, England</td></tr>\n      <tr><td>Key themes</td><td>Social responsibility, class inequality, guilt and denial, generational divide (the young accept responsibility; the old do not)</td></tr>\n      <tr><td>Dramatic technique</td><td>A \"well-made play\" structure with a single set and a mysterious investigator device</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. A Man for All Seasons — Robert Bolt</h3>\n<p class=\"learn-p\">The play dramatises the true story of <strong>Sir Thomas More</strong>, Lord Chancellor of England under King Henry VIII, who refuses to publicly support the King's divorce from Catherine of Aragon and his establishment of the Church of England, because doing so would violate More's Catholic conscience. Despite immense pressure from the King, his family, and even friends like the ambitious <strong>Richard Rich</strong> (who eventually betrays him with false testimony), More refuses to compromise his principles, choosing silence rather than an outright lie. He is ultimately tried, convicted through perjured evidence, and executed for treason.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Element</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Protagonist</td><td>Sir Thomas More</td></tr>\n      <tr><td>Key characters</td><td>King Henry VIII, Richard Rich, Thomas Cromwell, the Common Man (a narrator figure who plays multiple minor roles)</td></tr>\n      <tr><td>Setting</td><td>16th-century England, during the reign of Henry VIII</td></tr>\n      <tr><td>Key themes</td><td>Integrity and conscience, the danger of political ambition, the individual versus the state, hypocrisy</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Comparing the Plays:</strong> Both plays explore what happens when personal integrity clashes with social or political pressure. The Birlings choose denial over responsibility, while Thomas More chooses death over compromising his conscience — offering a striking moral contrast.\n</div>\n",
    "questions": [
      {
        "q": "Who wrote An Inspector Calls?",
        "o": [
          "J. B. Priestley",
          "Shakespeare",
          "Robert Bolt",
          "Harper Lee"
        ],
        "a": 0,
        "e": "An Inspector Calls was written by the English dramatist J. B. Priestley.",
        "h": "He was an English playwright.",
        "yr": "SS3"
      },
      {
        "q": "In what year is An Inspector Calls set?",
        "o": [
          "1600",
          "1945",
          "1912",
          "1850"
        ],
        "a": 2,
        "e": "The play is set in 1912 in the Birling family's dining room.",
        "h": "It is set before the First World War.",
        "yr": "SS3"
      },
      {
        "q": "Which family is at the centre of An Inspector Calls?",
        "o": [
          "The Birlings",
          "The Mores",
          "The Finches",
          "The Ewells"
        ],
        "a": 0,
        "e": "The wealthy Birling family is at the centre of the play.",
        "h": "They are celebrating an engagement.",
        "yr": "SS3"
      },
      {
        "q": "Who is the mysterious visitor who interrupts the family dinner?",
        "o": [
          "Gerald Croft",
          "Richard Rich",
          "Inspector Goole",
          "Thomas Cromwell"
        ],
        "a": 2,
        "e": "Inspector Goole arrives to investigate a young woman's death.",
        "h": "He is a police inspector.",
        "yr": "SS3"
      },
      {
        "q": "Whose death does the Inspector investigate?",
        "o": [
          "Desola",
          "Eva Smith (Daisy Renton)",
          "Mayella Ewell",
          "Sheila Birling"
        ],
        "a": 1,
        "e": "He investigates the suicide of Eva Smith, also known as Daisy Renton.",
        "h": "She is a young working-class woman.",
        "yr": "SS3"
      },
      {
        "q": "Who is Gerald Croft?",
        "o": [
          "Sheila's fiancé",
          "The Inspector's assistant",
          "Eric's tutor",
          "The family butler"
        ],
        "a": 0,
        "e": "Gerald Croft is Sheila Birling's fiancé.",
        "h": "The dinner celebrates their engagement.",
        "yr": "SS3"
      },
      {
        "q": "How does the Inspector reveal the family's guilt?",
        "o": [
          "By calling the police",
          "By arresting them all at once",
          "By reading a letter aloud",
          "One by one, showing each contributed to Eva's fate"
        ],
        "a": 3,
        "e": "He reveals each member's contribution, through selfishness or cruelty, to the chain of events that drove Eva to her death.",
        "h": "The questioning takes place in turn.",
        "yr": "SS3"
      },
      {
        "q": "Which members of the family show genuine remorse?",
        "o": [
          "Arthur and Sybil",
          "Sheila and Eric",
          "None of them",
          "Only Gerald"
        ],
        "a": 1,
        "e": "The younger generation, Sheila and Eric, accept responsibility, while their parents do not.",
        "h": "It is the generational divide.",
        "yr": "SS3"
      },
      {
        "q": "How does Arthur Birling react to the Inspector's revelations?",
        "o": [
          "He denies responsibility and worries about scandal",
          "He faints",
          "He confesses and apologises",
          "He leaves the house"
        ],
        "a": 0,
        "e": "Arthur denies responsibility and is chiefly concerned about scandal.",
        "h": "He is a self-satisfied businessman.",
        "yr": "SS3"
      },
      {
        "q": "What does the ambiguous ending question?",
        "o": [
          "Where Eric went",
          "Whether Eva ever lived",
          "Who won the election",
          "Whether the Inspector was real"
        ],
        "a": 3,
        "e": "It questions whether the Inspector was real, while the family's moral guilt remains.",
        "h": "It concerns the identity of the visitor.",
        "yr": "SS3"
      },
      {
        "q": "Which is a central theme of An Inspector Calls?",
        "o": [
          "Colonial rule",
          "Romantic love",
          "Supernatural revenge",
          "Social responsibility"
        ],
        "a": 3,
        "e": "Social responsibility and class inequality are central themes.",
        "h": "We are responsible for one another.",
        "yr": "SS3"
      },
      {
        "q": "What generational contrast does the play highlight?",
        "o": [
          "Neither generation cares",
          "The old are humble; the young arrogant",
          "Both generations are equally guilty and remorseful",
          "The young accept responsibility; the old do not"
        ],
        "a": 3,
        "e": "The young (Sheila, Eric) accept responsibility; the old (Arthur, Sybil) do not.",
        "h": "Look at the attitudes of parents and children.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote A Man for All Seasons?",
        "o": [
          "Efua Sutherland",
          "Bosede Ademilua-Afolayan",
          "J. B. Priestley",
          "Robert Bolt"
        ],
        "a": 3,
        "e": "A Man for All Seasons was written by Robert Bolt.",
        "h": "He is an English playwright.",
        "yr": "SS3"
      },
      {
        "q": "Who is the protagonist of A Man for All Seasons?",
        "o": [
          "Richard Rich",
          "Sir Thomas More",
          "Thomas Cromwell",
          "King Henry VIII"
        ],
        "a": 1,
        "e": "Sir Thomas More, Lord Chancellor of England, is the protagonist.",
        "h": "He is a man of conscience.",
        "yr": "SS3"
      },
      {
        "q": "Which monarch does More refuse to support?",
        "o": [
          "Elizabeth I",
          "King Henry VIII",
          "Edward VI",
          "Charles I"
        ],
        "a": 1,
        "e": "More refuses to support King Henry VIII's divorce and his break with the Church.",
        "h": "He is a Tudor king.",
        "yr": "SS3"
      },
      {
        "q": "Why does More refuse to endorse the King's actions?",
        "o": [
          "He is afraid of Cromwell",
          "He wants to be king",
          "He supports the divorce privately",
          "It would violate his Catholic conscience"
        ],
        "a": 3,
        "e": "Endorsing the divorce and the new Church would violate More's Catholic conscience.",
        "h": "It is a matter of principle.",
        "yr": "SS3"
      },
      {
        "q": "What does Richard Rich do to More?",
        "o": [
          "Helps him escape",
          "Defends him at trial",
          "Marries his daughter",
          "Betrays him with false testimony"
        ],
        "a": 3,
        "e": "Rich, ambitious and corrupt, gives perjured evidence that convicts More.",
        "h": "He is a self-serving friend.",
        "yr": "SS3"
      },
      {
        "q": "What is the Common Man in the play?",
        "o": [
          "The King's brother",
          "A judge",
          "More's servant only",
          "A narrator figure who plays multiple minor roles"
        ],
        "a": 3,
        "e": "The Common Man is a narrator figure who plays several minor roles.",
        "h": "He comments directly on the action.",
        "yr": "SS3"
      },
      {
        "q": "What is More's fate?",
        "o": [
          "He becomes Chancellor again",
          "He flees to France",
          "He is pardoned",
          "He is convicted of treason and executed"
        ],
        "a": 3,
        "e": "More is tried, convicted through perjured evidence, and executed for treason.",
        "h": "He dies for his principles.",
        "yr": "SS3"
      },
      {
        "q": "Which theme links both non-African plays?",
        "o": [
          "Personal integrity versus social or political pressure",
          "Fantasy and magic",
          "Economic trade",
          "Colonial resistance"
        ],
        "a": 0,
        "e": "Both explore integrity versus pressure: the Birlings choose denial, More chooses death over compromise.",
        "h": "Compare the Birlings and More.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "African and Non-African Poetry — Selected Poems",
    "topicCode": "SS3-LIT-14",
    "module": "Poetry",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic reviews all twelve prescribed poems — six African and six non-African — focusing on their themes, tone, and poetic devices.\n</div>\n\n<h3 class=\"learn-subheading\">1. African Poetry</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Poem &amp; Poet</th><th>Theme / Focus</th></tr></thead>\n    <tbody>\n      <tr><td>\"Once Upon a Time\" — Gabriel Okara</td><td>A father laments the loss of sincerity and warmth in adult life, contrasting fake modern \"face\" culture with his son's innocent openness; he longs to relearn genuine laughter.</td></tr>\n      <tr><td>\"New Tongue\" — Elizabeth L. A. Kamara</td><td>A new generation abandons traditional language, customs, and family ties in favour of a \"borrowed\" foreign culture, symbolising the erosion of heritage.</td></tr>\n      <tr><td>\"Night\" — Wole Soyinka</td><td>A dense, sensuous meditation on night as both a lover and a threatening presence, blending imagery of the sea, shadows, and hidden fears.</td></tr>\n      <tr><td>\"Not My Business\" — Niyi Osundare</td><td>A satirical warning against apathy toward injustice and political persecution — the speaker's indifference to others' suffering ends when he himself becomes a victim.</td></tr>\n      <tr><td>\"Hearty Garlands\" — S.O.H. Afriyie-Vidza</td><td>A celebratory birthday tribute to an elderly, accomplished mentor, honouring a long and fulfilled life at eighty-five.</td></tr>\n      <tr><td>\"The Breast of the Sea\" — Syl Cheney-Coker</td><td>Uses the image of the polluted, suffering sea to reflect on history's violence and Sierra Leone's own wounds, including war and exploitation.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Non-African Poetry</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Poem &amp; Poet</th><th>Theme / Focus</th></tr></thead>\n    <tbody>\n      <tr><td>\"She Walks in Beauty\" — Lord Byron</td><td>A tribute to a woman's perfect physical and inner beauty, comparing her to a cloudless, starry night.</td></tr>\n      <tr><td>\"The Nun's Priest's Tale\" (shortened) — Geoffrey Chaucer</td><td>A mock-heroic beast fable about a proud rooster, Chanticleer, and his favourite hen, Pertelote, satirising human vanity and pride through animal characters.</td></tr>\n      <tr><td>\"Digging\" — Seamus Heaney</td><td>The poet compares his pen and writing to his father's and grandfather's manual work with a spade, honouring their labour while affirming his own different path.</td></tr>\n      <tr><td>\"Still I Rise\" — Maya Angelou</td><td>A defiant, empowering declaration of resilience against oppression, racism, and attempts to diminish the speaker's dignity.</td></tr>\n      <tr><td>\"The Telephone Call\" — Fleur Adcock</td><td>A satirical poem about a hoax lottery call, exposing how easily people are manipulated by the promise of sudden wealth.</td></tr>\n      <tr><td>\"The Stone\" — Wilfrid Wilson Gibson</td><td>A grieving woman, turned numb with shock after her lover's death in a quarry accident, watches in silence as a stonecutter carves his name on a gravestone.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Comparative Notes</h3>\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  Group poems by shared themes for revision: loss of tradition/identity (\"Once Upon a Time,\" \"New Tongue\"), social/political injustice (\"Not My Business,\" \"The Breast of the Sea,\" \"Still I Rise\"), grief and loss (\"The Stone\"), and celebration/tribute (\"Hearty Garlands,\" \"She Walks in Beauty,\" \"Digging\").\n</div>\n\n<h3 class=\"learn-subheading\">4. Common Poetic Devices to Identify</h3>\n<p class=\"learn-p\">Look for: <strong>refrain</strong> (Osundare's repeated question after each stanza), <strong>extended metaphor</strong> (Heaney's pen-as-spade; Cheney-Coker's sea-as-body), <strong>satire and irony</strong> (Adcock's fake lottery call; Chaucer's mock-heroic tone), and <strong>anaphora/repetition</strong> for emphasis (Angelou's repeated declaration of rising).</p>\n",
    "questions": [
      {
        "q": "Who wrote 'Once Upon a Time'?",
        "o": [
          "Gabriel Okara",
          "Niyi Osundare",
          "Syl Cheney-Coker",
          "Wole Soyinka"
        ],
        "a": 0,
        "e": "'Once Upon a Time' was written by the Nigerian poet Gabriel Okara.",
        "h": "The poem is addressed to a son.",
        "yr": "SS3"
      },
      {
        "q": "What does the speaker of 'Once Upon a Time' long to relearn?",
        "o": [
          "How to speak a new language",
          "How to laugh genuinely",
          "How to dress well",
          "How to read"
        ],
        "a": 1,
        "e": "The father longs to relearn genuine laughter and unlearn insincere behaviour.",
        "h": "He wishes to be like his child again.",
        "yr": "SS3"
      },
      {
        "q": "What does 'Once Upon a Time' criticise?",
        "o": [
          "Colonial taxation",
          "Insincerity and hypocrisy in adult society",
          "Poverty",
          "War"
        ],
        "a": 1,
        "e": "It criticises insincerity, with people wearing false faces and hollow smiles.",
        "h": "Adults wear many 'faces'.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote 'New Tongue'?",
        "o": [
          "Maya Angelou",
          "Seamus Heaney",
          "Elizabeth L. A. Kamara",
          "Fleur Adcock"
        ],
        "a": 2,
        "e": "'New Tongue' was written by Elizabeth L. A. Kamara.",
        "h": "She is a Sierra Leonean poet.",
        "yr": "SS3"
      },
      {
        "q": "What is the main theme of 'New Tongue'?",
        "o": [
          "A celebration of nature",
          "Romantic longing",
          "Loss of cultural heritage and family ties in a new generation",
          "The joy of birthdays"
        ],
        "a": 2,
        "e": "It laments a generation abandoning its language, customs and family bonds for a borrowed culture.",
        "h": "Old songs are treated as remnants of the past.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote 'Night'?",
        "o": [
          "Wole Soyinka",
          "Gabriel Okara",
          "Niyi Osundare",
          "Lord Byron"
        ],
        "a": 0,
        "e": "'Night' was written by Nobel laureate Wole Soyinka.",
        "h": "He is a Nigerian Nobel Prize winner.",
        "yr": "SS3"
      },
      {
        "q": "How does the poem 'Night' present its subject?",
        "o": [
          "As a purely joyful time",
          "As a sensuous yet threatening presence",
          "As a political slogan",
          "As a religious hymn"
        ],
        "a": 1,
        "e": "Night is presented in dense, sensuous imagery as both intimate and menacing.",
        "h": "Think of the sea, shadows and hidden fears.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote 'Not My Business'?",
        "o": [
          "S.O.H. Afriyie-Vidza",
          "Syl Cheney-Coker",
          "Niyi Osundare",
          "Wilfrid Gibson"
        ],
        "a": 2,
        "e": "'Not My Business' was written by Niyi Osundare.",
        "h": "He is a Nigerian poet.",
        "yr": "SS3"
      },
      {
        "q": "What is the message of 'Not My Business'?",
        "o": [
          "Food is a blessing",
          "Minding one's business is wise",
          "Dictators are always defeated",
          "Indifference to others' suffering eventually leaves one a victim too"
        ],
        "a": 3,
        "e": "The speaker ignores injustice until the arrest squad finally comes for him.",
        "h": "Apathy has a price.",
        "yr": "SS3"
      },
      {
        "q": "Which device is central to 'Not My Business'?",
        "o": [
          "Rhyming couplets",
          "Refrain",
          "Onomatopoeia",
          "Sonnet form"
        ],
        "a": 1,
        "e": "A repeated refrain marks the speaker's selfish indifference.",
        "h": "A question is repeated after each stanza.",
        "yr": "SS3"
      },
      {
        "q": "What occasion does 'Hearty Garlands' celebrate?",
        "o": [
          "An eighty-fifth birthday",
          "A harvest festival",
          "A wedding",
          "A funeral"
        ],
        "a": 0,
        "e": "It is a birthday tribute to an elderly, accomplished mentor at eighty-five.",
        "h": "It honours a long life.",
        "yr": "SS3"
      },
      {
        "q": "What image does Cheney-Coker use in 'The Breast of the Sea'?",
        "o": [
          "A desert",
          "A city street",
          "A mountain",
          "The polluted, suffering sea as a body"
        ],
        "a": 3,
        "e": "The sea is likened to a suffering body to reflect history's violence and Sierra Leone's wounds.",
        "h": "It is an extended metaphor.",
        "yr": "SS3"
      },
      {
        "q": "Who wrote 'She Walks in Beauty'?",
        "o": [
          "Maya Angelou",
          "Geoffrey Chaucer",
          "Seamus Heaney",
          "Lord Byron"
        ],
        "a": 3,
        "e": "'She Walks in Beauty' was written by Lord Byron.",
        "h": "He is a Romantic poet.",
        "yr": "SS3"
      },
      {
        "q": "What does Byron compare the woman's beauty to?",
        "o": [
          "A garden in spring",
          "A cloudless, starry night",
          "A stormy sea",
          "A blazing sun"
        ],
        "a": 1,
        "e": "He compares her to the night of cloudless climes and starry skies, blending dark and bright.",
        "h": "It is a night image.",
        "yr": "SS3"
      },
      {
        "q": "What kind of poem is 'The Nun's Priest's Tale'?",
        "o": [
          "A mock-heroic beast fable satirising vanity",
          "A war epic",
          "A funeral elegy",
          "A love sonnet"
        ],
        "a": 0,
        "e": "It is a mock-heroic beast fable about the rooster Chanticleer, satirising human pride.",
        "h": "Animals speak and act like people.",
        "yr": "SS3"
      },
      {
        "q": "In 'Digging', what does the poet compare his pen to?",
        "o": [
          "A hammer",
          "A sword",
          "A plough",
          "A spade"
        ],
        "a": 3,
        "e": "Heaney likens his pen to his father's and grandfather's spade.",
        "h": "It links writing and manual labour.",
        "yr": "SS3"
      },
      {
        "q": "What is the message of 'Digging'?",
        "o": [
          "Rejecting his family",
          "Praising farming over writing",
          "Honouring family labour while choosing his own path as a writer",
          "Regret at leaving Ireland"
        ],
        "a": 2,
        "e": "He honours his forebears' labour while affirming that he will 'dig' with his pen.",
        "h": "He follows a different path.",
        "yr": "SS3"
      },
      {
        "q": "What is the tone of Maya Angelou's 'Still I Rise'?",
        "o": [
          "Humorous and light",
          "Fearful",
          "Defiant and empowering",
          "Sorrowful and defeated"
        ],
        "a": 2,
        "e": "It is a defiant declaration of resilience against oppression and racism.",
        "h": "The speaker refuses to be diminished.",
        "yr": "SS3"
      },
      {
        "q": "What is 'The Telephone Call' about?",
        "o": [
          "A hoax lottery call that satirises manipulation by promises of wealth",
          "A telephone repair",
          "A job offer",
          "A friend's bad news"
        ],
        "a": 0,
        "e": "It satirises how people are manipulated by the promise of sudden riches; the prize is only an 'experience'.",
        "h": "There is no cheque.",
        "yr": "SS3"
      },
      {
        "q": "What happens in Gibson's 'The Stone'?",
        "o": [
          "A child finds a gem",
          "A farmer builds a wall",
          "A grieving woman watches a stonecutter carve her dead lover's gravestone",
          "A sculptor wins a prize"
        ],
        "a": 2,
        "e": "Numbed by grief after her lover's quarry death, the woman silently watches the stone being cut for him.",
        "h": "It is a poem of grief and loss.",
        "yr": "SS3"
      }
    ]
  }
];
